import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GLOBAL_FILTER_EVENTS } from '@/features/global-filter/constants/events';
import { type SelectionMap } from '@/core/components/FilterSelector/types';
import { FilterHeader } from './Header/FilterHeader';
import { FilterFooter, SaveResult, ContentDuplicateData } from './Footer/FilterFooter';
import { FilterSection } from './Section/FilterSection';
import {
  resolveConfigFromDOM,
  isMetricsFieldRestrictedContext,
  type ResolvedFilterConfig,
  type FilterSectionName,
} from './filterConfig';
import {
  ClassificationContent,
  ClassificationData,
  defaultClassificationData,
} from './sections-content/classificationContent';
import { type NormalizedSentiment } from '@/core/constants/domainMappings';
import { ContentSection, ContentData, defaultValues, ALL_CONTENT_TYPE_IDS } from './sections-content/ContentSection/ContentSection';
import { InteractionsContent, InteractionsData } from './sections-content/interactionsContent/InteractionsContent';
import { isInteractionRangeFiltered } from './sections-content/interactionsContent/InteractionsContent.defaults';
import { ConversationContent, ConversationData, defaultConversationValues } from './sections-content/ConversationContent';
import { PeriodContent, PeriodData } from './sections-content/PeriodContent/PeriodContent';
import { OriginContent } from './sections-content/originContent/OriginContent';
import {
  buildOriginDefaultData,
  LANGUAGE_OPTIONS,
  type OriginData,
  type FilterOption,
} from './sections-content/originContent/OriginContentDefaults';
import { AuthorContent, availableProfileTypesKeys } from './sections-content/authorsContent/AuthorsContent';
import {
  buildAuthorDefaultData,
  buildAuthorEncodePayload,
  applyMyPagesFilterToData,
  getAuthorKey,
  GENDER_NUMBERS,
  FOLLOWERS_MIN,
  FOLLOWERS_MAX,
  type AuthorData,
  type MyPagesFilterType,
} from './sections-content/authorsContent/AuthorsContent.defaults';
import { buildConversationPayload } from '../utils/buildConversationPayload';
import { buildRangePayload } from '@/core/components/RangePayload/RangePayload';
import { validateTextualSearch } from '@/core/utils/textualSearchValidator';
import { UnsavedChangesAlert } from './UnsavedChangesAlert/UnsavedChangesAlert';
import savedFiltersService, { type SavedFilter } from '@/core/services/SavedFiltersService';
import { type FilterEncodeData } from '@/core/services/GlobalFilterService';
import { useSavedFilters } from './hooks/useSavedFilters';
import { useSavedFiltersCache } from '@/core/hooks/useSavedFiltersCache';
import globalSavedFiltersService from '@/core/services/GlobalSavedFiltersService';
import { useSelectorItems } from '@/core/hooks/useSelectorItems';
import userService from '@/core/services/UserService';
import { parseDateRange, formatRangeString, getCalculatedDates, clampToMaxRangeDays, RESTRICTED_MAX_RANGE_DAYS } from '@/core/utils/dateUtils';
import { fetchConversationRoutingByAccount } from '@/core/services/ConversationRoutingService';
import { fetchConversationPagesByAccount, type ConversationPageAccountItem } from '@/core/services/ConversationPagesService';
import { useToast } from '@/core/components/ToastProvider/ToastProvider';
import { getAccountId } from '@/core/utils/extractCookieId';
import { CONFIG } from '@/config/Config';
import { getChannels, type Channel, type ChannelLabels } from '@/core/services/ChannelsService';
import { getInteractionValuesFromChannels } from '@/features/global-filter/constants/interactionsByChannel';
import {
  POLARITY_TO_NORMALIZED_SENTIMENT,
  NORMALIZED_SENTIMENT_TO_POLARITY,
  GENDER_NUMBER_TO_I18N_KEY,
  CONVERSATION_CRITICALITY_VALUE_TO_KEY,
  CONVERSATION_STATUS_VALUE_TO_KEY,
} from '@/core/constants/domainMappings';
import './GlobalFilterModal.scss';
import { AuthorOption } from '@/core/services/interfaces/AuthorInterfaces';

interface GlobalFilterModalProps {
  isExportMode?: boolean;
  onClose?: () => void;
  excludedChannelKeys?: string[];
}

interface GlobalParametersShape {
  account?: {
    name?: string;
    countPagesAccountConnected?: number | string;
  };
  universe?: {
    name?: string;
  };
}

const MIN = 0;
const MAX = 5000000;

const getChannelLabelForLang = (channel: Channel, language: string): string => {
  const labels: ChannelLabels = channel.labels;
  if (language.startsWith('es')) return labels.es || labels.pt || labels.en || channel.key;
  if (language.startsWith('en')) return labels.en || labels.pt || labels.es || channel.key;
  return labels.pt || labels.en || labels.es || channel.key;
};

const CHANNEL_LABEL_OVERRIDES: Record<string, ChannelLabels> = {
  twitter: {
    pt: 'X (Twitter)',
    en: 'X (Twitter)',
    es: 'X (Twitter)',
  },
  Twitter: {
    pt: 'X (Twitter)',
    en: 'X (Twitter)',
    es: 'X (Twitter)',
  },
  TwitterInbox: {
    pt: 'Inbox do X (Twitter)',
    en: 'X (Twitter) Inbox',
    es: 'X (Twitter) Inbox',
  },
};

const getChannelLabelWithOverride = (channel: Channel, language: string): string => {
  const channelToUse = CHANNEL_LABEL_OVERRIDES[channel.key]
    ? { ...channel, labels: CHANNEL_LABEL_OVERRIDES[channel.key] }
    : channel;
  return getChannelLabelForLang(channelToUse, language);
};

const getDefaultAuthorData = (): AuthorData =>
  buildAuthorDefaultData(availableProfileTypesKeys);

const createDefaultInteractionsData = (): InteractionsData => ({
  likes: [MIN, MAX],
  shares: [MIN, MAX],
  views: [MIN, MAX],
  comments: [MIN, MAX],
});

const buildChannelSmartcareMap = (channels: Channel[]): Map<string, boolean> =>
  new Map(channels.map((channel) => [channel.key.toLowerCase(), channel.support?.smartcare !== false]));

const getActiveUniverseId = (): string | undefined =>
  CONFIG.cookieOrParam(CONFIG.AUTH.UNI_ID)?.split('|')[0] || undefined;

const isPageLinkedToActiveUniverse = (
  page: ConversationPageAccountItem,
  activeUniverseId?: string,
): boolean => {
  if (!activeUniverseId) return true;
  return page.linkedUniverses?.some((id) => String(id) === String(activeUniverseId)) ?? false;
};

const filterSmartcarePages = (
  pages: ConversationPageAccountItem[],
  channelSmartcareMap: Map<string, boolean>,
  activeUniverseId?: string,
): ConversationPageAccountItem[] =>
  pages.filter((page) => {
    const channelKey = page.channel.toLowerCase();
    if (channelSmartcareMap.get(channelKey) === false) return false;
    return isPageLinkedToActiveUniverse(page, activeUniverseId);
  });

const mapConnectedPagesAsAuthors = (pages: ConversationPageAccountItem[]): AuthorOption[] =>
  pages.map((page) => ({
    uid: String(page.id),
    uids: [String(page.id)],
    name: page.title || page.username || '',
    username: page.username || page.title || '',
    channel: page.channel,
    followers: 0,
    isVerified: false,
    profilePicture: '',
  }));

const filterExcludedChannels = (channels: Channel[], excludedChannelKeys: string[]): Channel[] => {
  const excludeSet = new Set(excludedChannelKeys.map((key) => key.toLowerCase()));
  return channels.filter((channel) => !(excludeSet.size > 0 && excludeSet.has(channel.key.toLowerCase())));
};

function mergeDecodedFilterObjs(
  prev: Record<string, Record<string, unknown>>,
  results: readonly (readonly [string, Record<string, unknown> | undefined])[],
): Record<string, Record<string, unknown>> {
  const next = { ...prev };
  let changed = false;
  for (const [id, decoded] of results) {
    if (!decoded) continue;
    next[id] = decoded;
    changed = true;
  }
  return changed ? next : prev;
}

function resolveFilterObj(f: any): Record<string, unknown> {
  const raw = f?.filterObj ?? f?.filter;
  if (!raw) return {};
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return typeof raw === 'object' ? raw : {};
}

// The /saved-filters list omits filterObj for large payloads (e.g. filters with many selected
// authors), so falling back to resolveFilterObj's "{}" would misread is_sac as falsy and list a
// proprietary filter as open sea. Use the lazily decoded filterObj instead, and treat the filter
// as unclassified (excluded from both lists) until the decode resolves.
function resolveKnownFilterObj(
  f: any,
  decodedFilterObjs: Record<string, Record<string, unknown>>,
): Record<string, unknown> | undefined {
  const inline = resolveFilterObj(f);
  if (Object.keys(inline).length > 0) return inline;
  return decodedFilterObjs[f?.id];
}

function extractArrayValue(obj: any): string[] {
  return (obj as { value: string[] } | undefined)?.value ?? [];
}

function checkPidsValue(obj: any): boolean {
  return obj?.value && Array.isArray(obj.value) && obj.value.length > 0;
}

export const toSelectionMap = (arr: string[]): SelectionMap => {
  if (!arr.length) return {};
  return Object.fromEntries(
    arr.map((n) => (n.startsWith('-') ? [n.slice(1), 'remove' as const] : [n, 'add' as const])),
  );
};

export const toFilterValues = (map: SelectionMap): string[] =>
  Object.entries(map).map(([name, action]) => (action === 'add' ? name : `-${name}`));

function buildClassificationDataFromFilter(filterObj: FilterEncodeData): ClassificationData {
  const classData: ClassificationData = { ...defaultClassificationData };
  const polarities = extractArrayValue(filterObj.polarities);
  if (filterObj.polarities !== undefined) {
    classData.sentiments = new Set(
      polarities.map((p) => POLARITY_TO_NORMALIZED_SENTIMENT[p]).filter(Boolean) as NormalizedSentiment[],
    );
    classData.sentimentsTouched = true;
  }
  if (filterObj.names !== undefined) {
    classData.groups = toSelectionMap(extractArrayValue(filterObj.names));
    classData.groupsTouched = true;
  }
  if (filterObj.themes !== undefined) {
    classData.themes = toSelectionMap(extractArrayValue(filterObj.themes));
    classData.themesTouched = true;
  }
  const tags = extractArrayValue(filterObj.tags);
  if (tags.length) classData.tags = toSelectionMap(tags);
  // Classified by is sent as edited_by (chronological date) or annotators (classification date).
  const editedBy = extractArrayValue(filterObj.edited_by);
  const annotators = extractArrayValue(filterObj.annotators);
  if (editedBy.length || annotators.length) {
    classData.classifiedBy = toSelectionMap(editedBy.length ? editedBy : annotators);
    classData.classifiedByTouched = true;
  }
  const selectionName = (filterObj.selection as { value: string } | undefined)?.value;
  if (selectionName) classData.selection = { [selectionName]: 'add' };
  const intersectionValues = (filterObj.intersection as { value: string[] } | undefined)?.value ?? [];
  if (intersectionValues.includes('tags')) classData.tagsIntersection = true;
  classData.status = buildStatusDataFromFilter(filterObj);
  return classData;
}

function buildStatusDataFromFilter(filterObj: FilterEncodeData): SelectionMap {
  const status: SelectionMap = {};
  const annotatedValue = (filterObj.annotated as { value?: boolean } | undefined)?.value;
  if (annotatedValue !== undefined) status[annotatedValue ? 'annotated' : 'notAnnotated'] = 'add';
  const favoriteValue = (filterObj.favorite as { value?: boolean } | undefined)?.value;
  if (favoriteValue !== undefined) status[favoriteValue ? 'checked' : 'notChecked'] = 'add';
  const deletedValue = (filterObj.deleted as { value?: boolean } | undefined)?.value;
  if (deletedValue) status.removed = 'add';
  return status;
}

function buildContentDataFromFilter(filterObj: FilterEncodeData): ContentData {
  let pubID = '';
  const pidsObj = filterObj.pids as { value: string[] } | { value: Record<string, never> };
  const postIdsObj = filterObj.post_ids as { value: string[] } | { value: Record<string, never> };
  const isPidsObj = checkPidsValue(pidsObj);
  const isPostIdsObj = checkPidsValue(postIdsObj);
  if (isPidsObj) {
    pubID = pidsObj.value.join(', ');
  } else if (isPostIdsObj) {
    pubID = postIdsObj.value.join(', ');
  }

  const contentData: ContentData = {
    ...defaultValues,
    textualSearch: (filterObj.query as { value: string } | undefined)?.value ?? '',
    publicationID: pubID,
  };

  const imageTextValue = (filterObj.has_image_text as { value: boolean } | undefined)?.value;
  if (typeof imageTextValue === 'boolean') {
    contentData.imageTranscriptionState = imageTextValue ? 'add' : 'remove';
    contentData.imageTranscription = imageTextValue;
  }
  const imageMatchOnlyValue = (filterObj.is_image_match_only as { value: boolean } | undefined)?.value;
  if (typeof imageMatchOnlyValue === 'boolean') {
    contentData.collectedByAIState = imageMatchOnlyValue ? 'add' : 'remove';
    contentData.collectedByAI = imageMatchOnlyValue;
  }
  const typesValue = (filterObj.types as { value: string[] } | undefined)?.value;
  if (typesValue && typesValue.length > 0) {
    contentData.contentType = new Set(typesValue.map((type) => (type === 'post' ? 'text' : type)));
  }
  const darkPostValue = (filterObj.dark_post as { value?: boolean } | undefined)?.value;
  if (typeof darkPostValue === 'boolean') {
    contentData.publicationType = { organic: !darkPostValue, darkPost: darkPostValue };
  }
  const includeMentionsValue = (filterObj.has_sac_filter as { value?: boolean } | undefined)?.value;
  if (includeMentionsValue) contentData.includeMentions = true;
  return contentData;
}

function buildInteractionsDataFromFilter(filterObj: FilterEncodeData): InteractionsData {
  const interactionsData: InteractionsData = createDefaultInteractionsData();
  for (const key of ['likes', 'shares', 'views', 'comments'] as const) {
    const obj = filterObj[key] as { value: { lte?: number | string; gte?: number | string } } | undefined;
    if (!obj?.value) continue;
    const gte = obj.value.gte === undefined ? MIN : Number(obj.value.gte);
    const lte = obj.value.lte === undefined ? MAX : Number(obj.value.lte);
    interactionsData[key] = [Number.isNaN(gte) ? MIN : gte, Number.isNaN(lte) ? MAX : lte];
  }
  return interactionsData;
}

function filterObjToState(filterObj: FilterEncodeData) {
  const classData = buildClassificationDataFromFilter(filterObj);
  const contentData = buildContentDataFromFilter(filterObj);
  const interactionsData = buildInteractionsDataFromFilter(filterObj);
  const authorData = mapAuthorFromFilterObj(filterObj);

  const isSac = filterObj.is_sac ?? false;

  // The API reuses "channels" for both the origin (open sea) selector and the conversations
  // (proprietary) selector - only hydrate whichever one is_sac actually refers to.
  const channelKeys = extractArrayValue(filterObj.channels);
  const originChannelKeys = isSac ? [] : channelKeys;
  const conversationChannels = isSac ? channelKeys : [];

  const channelsSelection = originChannelKeys.length
    ? Object.fromEntries(originChannelKeys.map((key) => [key, 'add' as const]))
    : {};

  const langIsos = extractArrayValue(filterObj.langs);
  const languagesSelection = langIsos.length
    ? Object.fromEntries(langIsos.map((iso) => [iso, 'add' as const]))
    : Object.fromEntries(LANGUAGE_OPTIONS.map((opt) => [opt.id, 'add' as const]));

  const locationValues = (filterObj.locations as { value?: Array<{ city?: string; state?: string; country?: string }> } | undefined)?.value ?? [];
  const selectedLocations = locationValues.map((loc) => ({
    country: loc.country ?? '',
    state: loc.state ?? '',
    ...(loc.city ? { city: loc.city } : {}),
  }));

  const originData: OriginData = {
    channelsSelection,
    languagesSelection,
    selectedLocations,
    languagesTouched: filterObj.langs !== undefined,
    channelsTouched: !isSac && filterObj.channels !== undefined,
  };

  const conversationPages = (filterObj.pages as { value: string[] } | undefined)?.value ?? [];
  const conversationInteractions = (filterObj.sac_type as { value: string[] } | undefined)?.value ?? [];
  const conversationCriticality = (filterObj.sac_critical_level as { value: string[] } | undefined)?.value ?? [];
  const conversationStatus = (filterObj.sac_status as { value: string[] } | undefined)?.value ?? [];
  const conversationRespondents = (filterObj.sac_replier as { value: string[] } | undefined)?.value ?? [];

  const routingField = filterObj.routing_id as { value?: string[] } | undefined;
  const conversationRouting = routingField?.value ?? [];

  const sacCallIdsField = filterObj.sac_call_ids as { value?: string[] } | undefined;
  let conversationId = '';
  if (sacCallIdsField?.value && Array.isArray(sacCallIdsField.value)) {
    conversationId = sacCallIdsField.value.join(', ');
  } else {
    conversationId = (filterObj.sac_id as { value?: string } | undefined)?.value ?? '';
  }
  const darkPost = (filterObj.dark_post as { value?: boolean } | undefined)?.value ?? false;

  const pageKeys = conversationPages.map((pageId, index) => {
    const channel = conversationChannels.length === 1 ? conversationChannels[0] : conversationChannels[index];
    if (!channel) return pageId;
    return pageId.toLowerCase().startsWith(`${channel.toLowerCase()}-`) ? pageId : `${channel}-${pageId}`;
  });

  // Use default pre-selected values when the filter doesn't specify any values
  const defaultCriticalityKeys = ['criticality-none', 'criticality-low', 'criticality-medium', 'criticality-high'];
  const defaultStatusKeys = ['status-ignored', 'status-pending', 'status-open', 'status-waiting', 'status-replied', 'status-closed'];

  const conversationData: ConversationData = {
    channels: Object.fromEntries(conversationChannels.map((channel) => [channel, 'add' as const])),
    pages: Object.fromEntries(pageKeys.map((key) => [key, 'add' as const])),
    interactions: Object.fromEntries(conversationInteractions.map((item) => [item, 'add' as const])),
    criticality: conversationCriticality.length > 0
      ? Object.fromEntries(
          conversationCriticality.map((value) => [CONVERSATION_CRITICALITY_VALUE_TO_KEY[value] ?? value, 'add' as const]),
        )
      : Object.fromEntries(defaultCriticalityKeys.map((key) => [key, 'add' as const])),
    status: conversationStatus.length > 0
      ? Object.fromEntries(
          conversationStatus.map((value) => [CONVERSATION_STATUS_VALUE_TO_KEY[value] ?? value, 'add' as const]),
        )
      : Object.fromEntries(defaultStatusKeys.map((key) => [key, 'add' as const])),
    respondent: Object.fromEntries(
      conversationRespondents.map((id) => [`respondent-${id}`, 'add' as const]),
    ),
    routing: Object.fromEntries(conversationRouting.map((id) => [id, 'add' as const])),
    conversationId,
    publicationType: {
      organic: false,
      darkPost,
    },
    channelsTouched: isSac && filterObj.channels !== undefined,
    pagesTouched: filterObj.pages !== undefined,
    interactionsTouched: filterObj.sac_type !== undefined,
    criticalityTouched: filterObj.sac_critical_level !== undefined,
    statusTouched: filterObj.sac_status !== undefined,
    respondentTouched: filterObj.sac_replier !== undefined,
    routingTouched: routingField !== undefined,
  };

  return {
    classData,
    contentData,
    interactionsData,
    originData,
    isSac,
    conversationData,
    authorData: authorData ?? undefined,
  };
}

const mapAuthorFromFilterObj = (
  filterObj: Record<string, unknown>,
): AuthorData | null => {
  const data = buildAuthorDefaultData(availableProfileTypesKeys);
  let modified = false;

  const occupationsObj = filterObj.occupations as { value?: unknown[] } | undefined;
  if (occupationsObj?.value?.length) {
    data.interestsSelection = Object.fromEntries(
      occupationsObj.value
        .map((value) => {
          const isValidPrimitive = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
          const raw = isValidPrimitive ? String(value).trim() : '';
          if (!raw) return null;
          return raw.startsWith('-')
            ? [raw.slice(1), 'remove' as const]
            : [raw, 'add' as const];
        })
        .filter((entry): entry is [string, 'add' | 'remove'] => !!entry),
    );
    modified = true;
  }

  const uidsObj = filterObj.uids as {
    value?: string[];
    users?: Array<{ c?: string; i?: string; n?: string; u?: string }>;
  } | undefined;

  if (uidsObj?.users?.length) {
    uidsObj.users.forEach((user) => {
      const rawUid = String(user?.i ?? '').trim();
      const rawName = String(user?.n ?? '').trim();
      const rawUsername = String(user?.u ?? '').trim();
      const rawChannel = String(user?.c ?? '').trim();

      if (!rawUid && !rawName && !rawUsername && !rawChannel) return;

      const uid = rawUid.replace(/^-+/, '');
      const isNegated = rawUid.startsWith('-');
      const cleanName = rawName.replace(/^-+/, '');

      const author: AuthorOption = {
        uid,
        uids: [uid],
        name: cleanName || rawUsername || rawChannel || uid,
        username: rawUsername || cleanName || uid,
        channel: rawChannel || 'unknown',
        followers: 0,
        isVerified: false,
        profilePicture: '',
      };

      const key = getAuthorKey(author);
      data.authorsMap[key] = author;
      data.authorsSelection[key] = isNegated ? 'remove' : 'add';
    });

    modified = true;
  }

  const gendersObj = filterObj.genders as { value?: number[] } | undefined;
  if (Array.isArray(gendersObj?.value)) {
    const selectedKeys = new Set(
      gendersObj.value
        .map((genderNumber) => GENDER_NUMBER_TO_I18N_KEY[genderNumber])
        .filter((key): key is string => !!key),
    );

    data.profileTypeSelection = Object.fromEntries(
      availableProfileTypesKeys
        .filter((key) => selectedKeys.has(key))
        .map((key) => [key, 'add' as const]),
    );
    data.profileTypeTouched = true;
    modified = true;
  }

  const followersObj = filterObj.followers as {
    value?: { gte?: string | number; lte?: string | number };
  } | undefined;

  if (followersObj?.value) {
    data.followersRange = [
      followersObj.value.gte == null ? MIN : Number(followersObj.value.gte),
      followersObj.value.lte == null ? MAX : Number(followersObj.value.lte),
    ];
    modified = true;
  }

  return modified ? data : null;
};

function buildPeriodDataForRange(dataRange: string, dataType = 'publication_date'): PeriodData {
  const calculatedDates = getCalculatedDates(dataRange);
  const start = calculatedDates?.start ?? new Date();
  const end = calculatedDates?.end ?? new Date();
  return {
    dataType,
    dataRange,
    formattedDateRange: formatRangeString(dataRange, start, end),
    startDate: start,
    endDate: end,
  };
}

function getDefaultPeriodData(isRestrictedDateMode: boolean): PeriodData {
  return buildPeriodDataForRange(isRestrictedDateMode ? 'last30d' : 'last7d');
}

function lockPeriodToLast24h(): PeriodData {
  return buildPeriodDataForRange('last24h', 'publication_date');
}

function exceedsRestrictedRange(period: PeriodData): boolean {
  if (!period.startDate || !period.endDate) return false;
  const spanMs = period.endDate.getTime() - period.startDate.getTime();
  return spanMs > RESTRICTED_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000;
}

function clampRestrictedPeriod(period: PeriodData): PeriodData {
  if (!period.startDate || !period.endDate) return getDefaultPeriodData(true);
  const { start, end } = clampToMaxRangeDays(period.startDate, period.endDate, RESTRICTED_MAX_RANGE_DAYS);
  return {
    dataType: period.dataType,
    dataRange: 'custom',
    formattedDateRange: formatRangeString('custom', start, end),
    startDate: start,
    endDate: end,
  };
}

function buildPeriodFromUrl(decodedFilter: FilterEncodeData | undefined): PeriodData | null {
  const params = new URLSearchParams(globalThis.location?.search ?? '');
  const urlDateRange = params.get('date_range');

  if (!urlDateRange) return null;

  const parsed = parseDateRange(urlDateRange);
  if (!parsed) return null;

  const isClassificationDate = (decodedFilter?.annotation_range as { value?: boolean } | undefined)?.value === true;
  const urlDateField = isClassificationDate ? 'classification_date' : 'publication_date';

  return {
    dataType: urlDateField,
    dataRange: parsed.dataRange,
    formattedDateRange: formatRangeString(parsed.dataRange, parsed.startDate, parsed.endDate),
    startDate: parsed.startDate,
    endDate: parsed.endDate,
  };
}

function resolveInitialPeriodData(
  urlPeriod: PeriodData | null,
  storedPeriod: PeriodData | undefined,
  isRestrictedDateMode: boolean,
): PeriodData {
  const resolved = urlPeriod ?? storedPeriod ?? getDefaultPeriodData(isRestrictedDateMode);
  return resolved;
}

const buildStatusPayload = (status: Record<string, string>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  const annotated = status['annotated'] === 'add';
  const notAnnotated = status['notAnnotated'] === 'add';
  if (annotated !== notAnnotated) result.annotated = { value: annotated };
  const checked = status['checked'] === 'add';
  const notChecked = status['notChecked'] === 'add';
  if (checked !== notChecked) result.favorite = { value: checked };
  if (status['removed'] === 'add') result.deleted = { value: true };
  return result;
};

const buildContentPayload = (data: ContentData, isSac: boolean): Record<string, unknown> => {
  const payload: Record<string, unknown> = {};
  const imageState = data.imageTranscriptionState ?? (data.imageTranscription ? 'add' : null);
  const collectedState = data.collectedByAIState ?? (data.collectedByAI ? 'add' : null);

  if (data.textualSearch) payload.query = { value: data.textualSearch };
  if (data.publicationID) {
    const ids = data.publicationID.split(',').map((id) => id.trim()).filter(Boolean);
    if (ids.length > 0) {
      payload[isSac ? 'post_ids' : 'pids'] = { value: ids };
    }
  }
  if (imageState !== null) {
    payload.has_image_text = { value: imageState === 'add' };
  }
  if (collectedState !== null) {
    payload.is_image_match_only = { value: collectedState === 'add' };
  }
  if (data.includeMentions) payload.has_sac_filter = { value: true };
  if (data.publicationType.darkPost !== data.publicationType.organic) {
    payload.dark_post = { value: data.publicationType.darkPost };
  }
  if (data.contentType.size > 0 && data.contentType.size < ALL_CONTENT_TYPE_IDS.size) {
    payload.types = {
      value: [...data.contentType].map((type) => (type === 'text' ? 'post' : type)),
    };
  }
  return payload;
};

const FILTER_STORAGE_KEY = 'globalFilterAppliedState';
const FILTER_STORAGE_VERSION = 1;
const AUTHOR_MY_PAGES_FILTER_STORAGE_PREFIX = 'globalFilterAuthorMyPagesFilter';

const LEGACY_DATE_RANGE_SESSION_KEY = 'date_range';

const getAuthorMyPagesFilterStorageKey = (filterId: string): string =>
  `${AUTHOR_MY_PAGES_FILTER_STORAGE_PREFIX}:${filterId}`;

function saveAuthorMyPagesFilterByFilterId(filterId: string, myPagesFilter: MyPagesFilterType): void {
  try {
    localStorage.setItem(getAuthorMyPagesFilterStorageKey(filterId), myPagesFilter);
  } catch (error) {
    console.error('localStorage write failed', error);
  }
}

function loadAuthorMyPagesFilterByFilterId(filterId: string): MyPagesFilterType | undefined {
  try {
    const stored = localStorage.getItem(getAuthorMyPagesFilterStorageKey(filterId));
    if (stored === 'all' || stored === 'myPages' || stored === 'notMyPages') {
      return stored;
    }
  } catch {
    return undefined;
  }
  return undefined;
}

function syncLegacyDateRangeSession(dateRange?: string): void {
  if (!dateRange) return;
  try {
    sessionStorage.setItem(LEGACY_DATE_RANGE_SESSION_KEY, dateRange);
  } catch (error) {
    console.error('sessionStorage write failed', error);
  }
}

type HydratedFilterState = {
  openSeaActive: boolean;
  proprietaryChannelsActive: boolean;
  classificationData: ClassificationData;
  contentData: ContentData;
  interactionsData: InteractionsData;
  originData: OriginData;
  conversationData: ConversationData;
  periodData: PeriodData | undefined;
  authorData?: AuthorData;
};

function serializeFilterState(state: HydratedFilterState): string {
  return JSON.stringify({
    version: FILTER_STORAGE_VERSION,
    openSeaActive: state.openSeaActive,
    proprietaryChannelsActive: state.proprietaryChannelsActive,
    classificationData: { ...state.classificationData, sentiments: [...state.classificationData.sentiments] },
    contentData: { ...state.contentData, contentType: [...state.contentData.contentType] },
    interactionsData: state.interactionsData,
    originData: state.originData,
    conversationData: state.conversationData,
    periodData: state.periodData
      ? { ...state.periodData, startDate: state.periodData.startDate?.toISOString(), endDate: state.periodData.endDate?.toISOString() }
      : undefined,
    authorData: state.authorData,
  });
}

function loadConsumerFilterState(filterId: string): HydratedFilterState | null {
  try {
    const stored = localStorage.getItem('filter');
    if (!stored) return null;
    const parsed = JSON.parse(stored) as { id?: string; filterObj?: Record<string, unknown> };
    if (!parsed.id || parsed.id !== filterId || !parsed.filterObj) return null;
    const { classData, contentData, interactionsData, originData, isSac, conversationData, authorData } =
      filterObjToState(parsed.filterObj as FilterEncodeData);
    return {
      openSeaActive: !isSac,
      proprietaryChannelsActive: isSac,
      classificationData: classData,
      contentData,
      interactionsData,
      originData,
      conversationData,
      periodData: undefined,
      authorData: authorData ?? undefined,
    };
  } catch {
    return null;
  }
}

function clampContentDataForConfig(data: ContentData, config: ResolvedFilterConfig): ContentData {
  if (!config.disableContentStilingueEyes && !config.disableContentIncludeMentions) return data;
  return {
    ...data,
    ...(config.disableContentStilingueEyes ? {
      imageTranscription: false,
      imageTranscriptionState: null,
      collectedByAI: false,
      collectedByAIState: null,
    } : {}),
    ...(config.disableContentIncludeMentions ? { includeMentions: false } : {}),
  };
}

function clampClassificationDataForConfig(data: ClassificationData, config: ResolvedFilterConfig): ClassificationData {
  if (!config.disableClassificationGroups) return data;
  return { ...data, groups: {}, groupsTouched: false, themes: {}, themesTouched: false };
}

function clampOriginDataForConfig(data: OriginData, config: ResolvedFilterConfig): OriginData {
  return config.disableOriginChannels ? { ...data, channelsSelection: {}, channelsTouched: false } : data;
}

// Restricts a channels selection map to keys present in the given available key list. Callers
// must only invoke this once the list has actually finished loading - an empty array here means
// "loaded with nothing available", and is pruned to an empty selection like any other case.
function pruneSelectionToAvailableKeys(selection: SelectionMap, availableKeys: string[]): SelectionMap {
  const availableSet = new Set(availableKeys);
  return Object.fromEntries(
    Object.entries(selection).filter(([key]) => availableSet.has(key)),
  );
}

// Restricts a channels selection map to keys present in the given account channel list. Returns
// the selection unchanged when the account channel list isn't loaded yet (empty).
function pruneChannelsSelectionToAvailable(
  channelsSelection: SelectionMap,
  availableChannels: Channel[],
): SelectionMap {
  return pruneSelectionToAvailableKeys(channelsSelection, availableChannels.map((ch) => ch.key));
}

function clampAuthorDataForConfig(data: AuthorData, config: ResolvedFilterConfig): AuthorData {
  return config.disableAuthorFollowers ? { ...data, followersRange: [FOLLOWERS_MIN, FOLLOWERS_MAX] } : data;
}

function clampConversationDataForConfig(data: ConversationData, config: ResolvedFilterConfig): ConversationData {
  if (!config.disableOtherConversationFields) return data;
  return {
    ...data,
    channels: {},
    pages: {},
    interactions: {},
    criticality: {},
    routing: {},
    conversationId: '',
    publicationType: { organic: false, darkPost: false },
    channelsTouched: false,
    pagesTouched: false,
    interactionsTouched: false,
    criticalityTouched: false,
    routingTouched: false,
  };
}

function sanitizeHydratedStateForConfig(
  state: HydratedFilterState,
  config: ResolvedFilterConfig,
): HydratedFilterState {
  return {
    ...state,
    contentData: clampContentDataForConfig(state.contentData, config),
    classificationData: clampClassificationDataForConfig(state.classificationData, config),
    originData: clampOriginDataForConfig(state.originData, config),
    conversationData: clampConversationDataForConfig(state.conversationData, config),
    authorData: state.authorData ? clampAuthorDataForConfig(state.authorData, config) : state.authorData,
  };
}

function restoreDisabledClassificationFields(
  data: ClassificationData,
  preserved: ClassificationData | undefined,
  config: ResolvedFilterConfig,
): ClassificationData {
  if (!config.disableClassificationGroups || !preserved) return data;
  return { ...data, groups: preserved.groups, groupsTouched: preserved.groupsTouched, themes: preserved.themes, themesTouched: preserved.themesTouched };
}

function restoreDisabledContentFields(
  data: ContentData,
  preserved: ContentData | undefined,
  config: ResolvedFilterConfig,
): ContentData {
  if (!preserved || (!config.disableContentStilingueEyes && !config.disableContentIncludeMentions)) return data;
  return {
    ...data,
    ...(config.disableContentStilingueEyes ? {
      imageTranscription: preserved.imageTranscription,
      imageTranscriptionState: preserved.imageTranscriptionState,
      collectedByAI: preserved.collectedByAI,
      collectedByAIState: preserved.collectedByAIState,
    } : {}),
    ...(config.disableContentIncludeMentions ? { includeMentions: preserved.includeMentions } : {}),
  };
}

function restoreDisabledOriginFields(
  data: OriginData,
  preserved: OriginData | undefined,
  config: ResolvedFilterConfig,
): OriginData {
  if (!config.disableOriginChannels || !preserved) return data;
  return { ...data, channelsSelection: preserved.channelsSelection, channelsTouched: preserved.channelsTouched };
}

function restoreDisabledAuthorFields(
  data: AuthorData,
  preserved: AuthorData | undefined,
  config: ResolvedFilterConfig,
): AuthorData {
  if (!config.disableAuthorFollowers || !preserved) return data;
  return { ...data, followersRange: preserved.followersRange };
}

function restoreDisabledConversationFields(
  data: ConversationData,
  preserved: ConversationData | undefined,
  config: ResolvedFilterConfig,
): ConversationData {
  if (!config.disableOtherConversationFields || !preserved) return data;
  return {
    ...data,
    channels: preserved.channels,
    pages: preserved.pages,
    interactions: preserved.interactions,
    criticality: preserved.criticality,
    routing: preserved.routing,
    conversationId: preserved.conversationId,
    publicationType: preserved.publicationType,
    channelsTouched: preserved.channelsTouched,
    pagesTouched: preserved.pagesTouched,
    interactionsTouched: preserved.interactionsTouched,
    criticalityTouched: preserved.criticalityTouched,
    routingTouched: preserved.routingTouched,
  };
}

function preserveHiddenSectionsForPersistence(
  current: {
    classificationData: ClassificationData;
    contentData: ContentData;
    interactionsData: InteractionsData;
    originData: OriginData;
    conversationData: ConversationData;
    authorData: AuthorData;
  },
  preserved: HydratedFilterState | null,
  config: ResolvedFilterConfig,
): typeof current {
  if (!preserved) return current;
  const isVisible = (section: FilterSectionName) => config.visibleSections.includes(section);

  let authorData = current.authorData;
  if (preserved.authorData) {
    const baseAuthorData = isVisible('author') ? current.authorData : preserved.authorData;
    authorData = restoreDisabledAuthorFields(baseAuthorData, preserved.authorData, config);
  }

  return {
    classificationData: restoreDisabledClassificationFields(
      isVisible('classification') ? current.classificationData : preserved.classificationData,
      preserved.classificationData,
      config,
    ),
    contentData: restoreDisabledContentFields(
      isVisible('content') ? current.contentData : preserved.contentData,
      preserved.contentData,
      config,
    ),
    interactionsData: isVisible('interactions') ? current.interactionsData : preserved.interactionsData,
    originData: restoreDisabledOriginFields(
      isVisible('context') ? current.originData : preserved.originData,
      preserved.originData,
      config,
    ),
    conversationData: restoreDisabledConversationFields(
      isVisible('conversations') ? current.conversationData : preserved.conversationData,
      preserved.conversationData,
      config,
    ),
    authorData,
  };
}

function loadRawFilterState(): HydratedFilterState | null {
  // Only load from URL parameters, never from saved localStorage state
  const filterId = new URLSearchParams(globalThis.location?.search ?? '').get('filters');
  return filterId ? loadConsumerFilterState(filterId) : null;
}

function loadInitialFilterState(config: ResolvedFilterConfig): HydratedFilterState | null {
  const raw = loadRawFilterState();
  return raw ? sanitizeHydratedStateForConfig(raw, config) : null;
}

const mapConversationPageToKey = (channel: string, id: string): string => `${channel.toLowerCase()}-${id}`;

const getGlobalParameters = (): GlobalParametersShape => {
  const withParams = globalThis as typeof globalThis & { GLOBAL_PARAMETERS?: GlobalParametersShape };
  return withParams.GLOBAL_PARAMETERS ?? {};
};

export const dispatchFilterEvent = (section: string, item: string, payload: any) => {
  globalThis.dispatchEvent(
    new CustomEvent(`GLOBAL_FILTER_${section.toUpperCase()}_${item.toUpperCase()}_CLICKED`, { detail: payload }),
  );
};

function sanitizeSavedFilterObj(
  filterObj: Record<string, unknown>,
  available: { groups: string[]; themes: string[]; tags: string[] },
): Record<string, unknown> {
  const sanitizeValues = (values: string[], availableSet: Set<string>): string[] =>
    values.filter((v) => availableSet.has(v.startsWith('-') ? v.slice(1) : v));

  const groupsSet = new Set(available.groups);
  const themesSet = new Set(available.themes);
  const tagsSet = new Set(available.tags);

  const result = { ...filterObj };

  const namesObj = filterObj.names as { value: string[] } | undefined;
  if (namesObj?.value?.length) {
    const filtered = sanitizeValues(namesObj.value, groupsSet);
    result.names = filtered.length ? { ...namesObj, value: filtered } : undefined;
  }

  const themesObj = filterObj.themes as { value: string[] } | undefined;
  if (themesObj?.value?.length) {
    const filtered = sanitizeValues(themesObj.value, themesSet);
    result.themes = filtered.length ? { ...themesObj, value: filtered } : undefined;
  }

  const tagsObj = filterObj.tags as { value: string[] } | undefined;
  if (tagsObj?.value?.length) {
    const filtered = sanitizeValues(tagsObj.value, tagsSet);
    result.tags = filtered.length ? { ...tagsObj, value: filtered } : undefined;
  }

  return result;
}

export const GlobalFilterModal: React.FC<GlobalFilterModalProps> = ({
  isExportMode = false,
  onClose = () => {},
  excludedChannelKeys: excludedChannelKeysProp = [],
}) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { encodeFilter, decodeFilter, createSavedFilter, updateSavedFilter, deleteSavedFilter } = useSavedFilters();
  const { groups: availableGroups, themes: availableThemes, tags: availableTags } = useSelectorItems();

  const resolvedConfig = useMemo<ResolvedFilterConfig>(
    () => resolveConfigFromDOM(),
    [],
  );

  const initialState = useMemo(() => loadInitialFilterState(resolvedConfig), [resolvedConfig]);

  // The raw "filters" (or legacy "filter") id from the URL, regardless of whether a "consumer"
  // localStorage state has already hydrated the sections below - the period's classification vs
  // publication date type must always be derived from this filter, even when initialState exists.
  const urlFilterId = useMemo(() => {
    const params = new URLSearchParams(globalThis.location?.search ?? '');
    return (params.get('filters') || params.get('filter'))?.trim() || null;
  }, []);

  // Same as urlFilterId, but null whenever a "consumer" localStorage state already hydrated the
  // sections below - this must block other effects from writing default values into fields it
  // will populate, while still letting the period effect above independently decode urlFilterId.
  const pendingUrlFilterId = useMemo(() => (initialState ? null : urlFilterId), [initialState, urlFilterId]);

  // Shared across the period and section hydration effects below so the same URL filter id is
  // only ever decoded once, no matter how many effects need its result.
  const pendingUrlFilterDecodePromiseRef = useRef<Promise<FilterEncodeData | undefined> | null>(null);
  const decodePendingUrlFilterOnce = useCallback((): Promise<FilterEncodeData | undefined> => {
    if (!urlFilterId) return Promise.resolve(undefined);
    pendingUrlFilterDecodePromiseRef.current ??= decodeFilter(urlFilterId);
    return pendingUrlFilterDecodePromiseRef.current;
  }, [urlFilterId, decodeFilter]);

  const preservedHiddenStateRef = useRef<HydratedFilterState | null>(null);

  preservedHiddenStateRef.current ??= loadRawFilterState();

  const isDateRestrictedMode = useMemo(
    () => resolvedConfig.mfeMode === 'date-only' || resolvedConfig.mfeMode === 'date-and-conversations',
    [resolvedConfig.mfeMode],
  );

  const sectionIsVisible = useCallback((name: FilterSectionName): boolean =>
    resolvedConfig.visibleSections.includes(name),
  [resolvedConfig.visibleSections]);

  const chipsAreLocked = resolvedConfig.allowedChips.length < 2;
  const chipsShouldControlSections = resolvedConfig.allowedChips.length >= 2;

  const profileTypeGenderMap = useMemo(() =>
    Object.fromEntries(
      availableProfileTypesKeys.map((key, index) => [key, GENDER_NUMBERS[index]]),
    ),
    [],
  );

  const [universeEditors, setUniverseEditors] = useState<{ key: string; label: string }[]>([]);

  useEffect(() => {
    const accountId = getAccountId();
    if (!accountId) return;
    userService.getAccountEditors(accountId)
      .then((editors) => {
        const sorted = [...editors].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' }));
        setUniverseEditors(sorted.map((user) => ({ key: String(user.id), label: user.name })));
      })
      .catch(() => {});
  }, []);

  const [allChannels, setAllChannels] = useState<Channel[]>([]);
  const allChannelsRef = useRef<Channel[]>(allChannels);
  useEffect(() => { allChannelsRef.current = allChannels; }, [allChannels]);
  const excludedChannelKeysRef = useRef<string[]>(excludedChannelKeysProp);
  useEffect(() => { excludedChannelKeysRef.current = excludedChannelKeysProp; }, [excludedChannelKeysProp]);

  const channelOptions = useMemo<FilterOption[]>(
    () => [...allChannels]
      .sort((a, b) => {
        const labelA = getChannelLabelWithOverride(a, i18n.language);
        const labelB = getChannelLabelWithOverride(b, i18n.language);
        const byLabel = labelA.localeCompare(labelB, i18n.language, { sensitivity: 'base' });
        return byLabel === 0 ? a.key.localeCompare(b.key, i18n.language, { sensitivity: 'base' }) : byLabel;
      })
      .map((ch) => ({
        id: ch.key,
        label: getChannelLabelWithOverride(ch, i18n.language),
      })),
    [allChannels, i18n.language]
  );

  const [activeTab, setActiveTab] = useState<'newSearch' | 'savedFilters'>('newSearch');
  const [isClosing, setIsClosing] = useState(false);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const [showUnsavedAlert, setShowUnsavedAlert] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingFilter, setEditingFilter] = useState<SavedFilter | null>(null);
  const [isEditDirty, setIsEditDirty] = useState(false);
  const [isNewSearchDirty, setIsNewSearchDirty] = useState(false);

  // Use cache hook for saved filters with auto-scoped key per account/universe
  const { 
    filters: savedFilters, 
    isLoading: isLoadingFilters, 
    fetchFilters: fetchSavedFilters, 
    invalidateCache: invalidateSavedFiltersCache 
  } = useSavedFiltersCache<SavedFilter>(() => savedFiltersService.getSavedFilters());

  // filterObj arrives inline for most saved filters, but the list endpoint omits it for large
  // payloads (e.g. filters with many selected authors). Decode those lazily so is_sac-based
  // classification below never has to guess (and default proprietary filters into open sea).
  const [decodedFilterObjs, setDecodedFilterObjs] = useState<Record<string, Record<string, unknown>>>({});
  const decodedFilterObjsRef = useRef(decodedFilterObjs);
  useEffect(() => { decodedFilterObjsRef.current = decodedFilterObjs; }, [decodedFilterObjs]);

  useEffect(() => {
    const pendingFilters = savedFilters.filter(
      (f) => Object.keys(resolveFilterObj(f)).length === 0 && !decodedFilterObjsRef.current[f.id],
    );
    if (pendingFilters.length === 0) return;

    let isMounted = true;
    void Promise.all(
      pendingFilters.map((f) =>
        decodeFilter(f.id)
          .then((decoded) => [f.id, decoded] as const)
          .catch(() => [f.id, undefined] as const),
      ),
    ).then((results) => {
      if (!isMounted) return;
      setDecodedFilterObjs((prev) => mergeDecodedFilterObjs(prev, results));
    });

    return () => { isMounted = false; };
  }, [savedFilters, decodeFilter]);

  const [selectedFilter, setSelectedFilter] = useState('');

  const [openSections, setOpenSections] = useState<Set<string>>(() => {
    if (resolvedConfig.mfeMode === 'date-and-conversations') {
      return new Set(['period', 'conversations']);
    }
    // Period section should always be expanded
    return new Set(['period']);
  });
  const openSectionsRef = useRef(openSections);

  useEffect(() => {
    openSectionsRef.current = openSections;
  }, [openSections]);

  const openSeaAllowed = resolvedConfig.allowedChips.includes('open-sea');
  const proprietaryAllowed = resolvedConfig.allowedChips.includes('proprietary');

  const hasStrictConfig = resolvedConfig.allowedChips.length < 2 || resolvedConfig.mfeMode !== 'full-filter';

  const initialOpenSeaActive = hasStrictConfig
    ? openSeaAllowed || !proprietaryAllowed
    : (initialState?.openSeaActive ?? true);

  const initialProprietaryActive = hasStrictConfig
    ? proprietaryAllowed && !openSeaAllowed
    : (initialState?.proprietaryChannelsActive ?? false);

  const [openSeaActive, setOpenSeaActive] = useState(initialOpenSeaActive);
  const [proprietaryChannelsActive, setProprietaryChannelsActive] = useState(initialProprietaryActive);

  const classifiedByItems = universeEditors;

  const [classificationData, setClassificationData] = useState<ClassificationData>(
    () => initialState?.classificationData ?? defaultClassificationData,
  );
  const [classificationBadgeCount, setClassificationBadgeCount] = useState(0);

  const [contentData, setContentData] = useState<ContentData>(
    () => initialState?.contentData ?? defaultValues,
  );
  const [contentBadgeCount, setContentBadgeCount] = useState(0);
  const [contentResetKey, setContentResetKey] = useState(0);
  const [conversationResetKey, setConversationResetKey] = useState(0);
  const [textualSearchError, setTextualSearchError] = useState('');

  const [interactionsData, setInteractionsData] = useState<InteractionsData>(
    () => initialState?.interactionsData ?? createDefaultInteractionsData(),
  );
  const [interactionsBadgeCount, setInteractionsBadgeCount] = useState(0);

  // Tracks whether the user (or a saved-filter/reset action) has replaced the period since
  // mount, so the async annotation_range correction below never clobbers a later edit.
  const periodDataTouchedRef = useRef(false);

  const [periodData, setPeriodData] = useState<PeriodData | undefined>(() => {
    // date_range itself is parsed synchronously from the URL - only its dataType (publication vs
    // classification date) depends on the async filter decode, corrected once it resolves below.
    const urlPeriod = buildPeriodFromUrl(undefined);
    const resolved = resolveInitialPeriodData(urlPeriod, initialState?.periodData, isDateRestrictedMode);
    const exceeds = isDateRestrictedMode && exceedsRestrictedRange(resolved);
    const clamped = exceeds ? clampRestrictedPeriod(resolved) : resolved;
    return resolvedConfig.lockPeriodTo24h ? lockPeriodToLast24h() : clamped;
  });

  const lockPeriodTo24h = resolvedConfig.lockPeriodTo24h;
  const updatePeriodData = useCallback((next: PeriodData | undefined) => {
    setPeriodData(lockPeriodTo24h ? lockPeriodToLast24h() : next);
  }, [lockPeriodTo24h]);

  useEffect(() => {
    const urlDateRange = new URLSearchParams(globalThis.location?.search ?? '').get('date_range');
    if (!urlDateRange) return;
    void decodePendingUrlFilterOnce().then((decodedFilter) => {
      const isClassificationDate = (decodedFilter?.annotation_range as { value?: boolean } | undefined)?.value === true;
      if (!isClassificationDate || periodDataTouchedRef.current || lockPeriodTo24h) return;
      setPeriodData((prev) => (prev ? { ...prev, dataType: 'classification_date' } : prev));
    });
  }, [decodePendingUrlFilterOnce, lockPeriodTo24h]);

  const periodDataRef = useRef(periodData);
  useEffect(() => {
    periodDataRef.current = periodData;
  }, [periodData]);

  const [originData, setOriginData] = useState<OriginData>(
    () => initialState?.originData ?? buildOriginDefaultData(),
  );
  const [originBadgeCount, setOriginBadgeCount] = useState(0);

  const [conversationData, setConversationData] = useState<ConversationData>(
    () => initialState?.conversationData ?? defaultConversationValues,
  );
  const [conversationAvailableChannels, setConversationAvailableChannels] = useState<string[]>([]);
  const conversationAvailableChannelsRef = useRef<string[]>(conversationAvailableChannels);
  useEffect(() => { conversationAvailableChannelsRef.current = conversationAvailableChannels; }, [conversationAvailableChannels]);
  const [conversationAvailablePages, setConversationAvailablePages] = useState<string[]>([]);
  const [conversationAvailableInteractions, setConversationAvailableInteractions] = useState<string[]>([]);
  const [hasCheckedConversationPages, setHasCheckedConversationPages] = useState(false);
  // Mirrors hasCheckedConversationPages for effects/callbacks whose closures can't see state
  // updates (e.g. the mount-only URL filter decode effect below).
  const hasCheckedConversationPagesRef = useRef(hasCheckedConversationPages);
  useEffect(() => { hasCheckedConversationPagesRef.current = hasCheckedConversationPages; }, [hasCheckedConversationPages]);
  const [conversationAvailableRespondents, setConversationAvailableRespondents] = useState(0);
  const [conversationAvailableRouting, setConversationAvailableRouting] = useState(0);
  const [conversationBadgeCount, setConversationBadgeCount] = useState(0);
  const [routingNameById, setRoutingNameById] = useState<Record<string, string>>({});

  const proprietaryChipDisabledReason = useMemo(() => {
    if (!hasCheckedConversationPages) return null;

    const pagesConnectedToUniverse = conversationAvailablePages.length > 0;
    const connectedPageChannels = new Set(
      conversationAvailablePages
        .map((pageKey) => pageKey.split('-')[0]?.toLowerCase())
        .filter(Boolean),
    );

    const isGoogleMyBusinessOn = allChannels.some((channel) => channel.key.toLowerCase() === 'googlemybusiness');
    const isGooglePlayOn = allChannels.some((channel) => channel.key.toLowerCase() === 'googleplay');

    const gmbAvailableAndPagesConnected =
      isGoogleMyBusinessOn && connectedPageChannels.has('googlemybusiness');
    const gpAvailableAndPagesConnected =
      isGooglePlayOn && connectedPageChannels.has('googleplay');

    if (pagesConnectedToUniverse || gmbAvailableAndPagesConnected || gpAvailableAndPagesConnected) {
      return null;
    }

    const globalParameters = getGlobalParameters();
    const universeName = globalParameters.universe?.name ?? '';
    const accountName = globalParameters.account?.name ?? '';
    const countPages = Number(globalParameters.account?.countPagesAccountConnected ?? 0);

    if (countPages > 0) {
      return t(
        'globalFilter.notifications.sac_tab_no_linked_pages_with_connected_account',
        {
          countPages,
          accountName,
          universeName,
          defaultValue:
            'Você possui {{countPages}} página(s) proprietária(s) conectada(s) à conta "{{accountName}}", mas nenhuma vinculada à pesquisa "{{universeName}}". Para utilizar este filtro, acesse o módulo Minhas Conexões e faça a configuração.',
        },
      );
    }

    return t(
      'globalFilter.notifications.sac_tab_no_connected_pages',
      {
        accountName,
        universeName,
        defaultValue:
          'Você não possui páginas proprietárias conectadas à conta {{accountName}}. Acesse o módulo Minhas Conexões para adicionar uma e a vincule à pesquisa "{{universeName}}".',
      },
    );
  }, [conversationAvailablePages, allChannels, hasCheckedConversationPages, t]);

  const [authorData, setAuthorData] = useState<AuthorData>(
    () => initialState?.authorData ?? getDefaultAuthorData(),
  );
  const [authorBadgeCount, setAuthorBadgeCount] = useState(0);
  const [connectedPagesAsAuthors, setConnectedPagesAsAuthors] = useState<AuthorOption[]>([]);

const filteredConnectedPages = useMemo<AuthorOption[]>(() => {
    if (!conversationData.pagesTouched) return connectedPagesAsAuthors;

    const selectedPageKeys = Object.entries(conversationData.pages)
      .filter(([, action]) => String(action).toLowerCase() === 'add')
      .map(([key]) => key.toLowerCase());

    if (selectedPageKeys.length === 0) {
      return [];
    }

    const selectedPageSet = new Set(selectedPageKeys);
    return connectedPagesAsAuthors.filter((page) => {
      const channelKey = page.channel.toLowerCase();
      const uidKey = String(page.uid).toLowerCase();
      const prefixedKey = `${channelKey}-${uidKey}`;
      
      return selectedPageSet.has(prefixedKey) || selectedPageSet.has(uidKey);
    });
  }, [connectedPagesAsAuthors, conversationData.pages, conversationData.pagesTouched]);

  const prevFilteredConnectedPagesRef = useRef(filteredConnectedPages);
  useEffect(() => {
    const prev = prevFilteredConnectedPagesRef.current;
    prevFilteredConnectedPagesRef.current = filteredConnectedPages;
    if (prev === filteredConnectedPages) return;
    setAuthorData((current) => {
      if (!current.myPagesFilter || current.myPagesFilter === 'all') return current;
      return applyMyPagesFilterToData(current, current.myPagesFilter, filteredConnectedPages);
    });
  }, [filteredConnectedPages]);

  const [isSaveChecked, setIsSaveChecked] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [saveResult, setSaveResult] = useState<SaveResult>(null);
  const [contentDuplicateData, setContentDuplicateData] = useState<ContentDuplicateData | null>(null);

  useEffect(() => {
    const { groups, themes, tags, classifiedBy, status, sentiments, sentimentsTouched, tagsIntersection } = classificationData;
    const isFiltered = (sel: Record<string, string>, availableLen: number) =>
      Object.keys(sel).length > 0 &&
      (availableLen === 0 || Object.keys(sel).length < availableLen || Object.values(sel).some((a) => a !== 'add'));
    const effectiveSentimentsCount = (!sentimentsTouched && sentiments.size === 0) ? 3 : sentiments.size;
    setClassificationBadgeCount(
      (isFiltered(groups, availableGroups.length) ? 1 : 0) +
      (isFiltered(themes, availableThemes.length) ? 1 : 0) +
      (Object.keys(tags).length > 0 ? 1 : 0) +
      (tagsIntersection ? 1 : 0) +
      (Object.keys(classifiedBy).length > 0 ? 1 : 0) +
      (Object.keys(status).length > 0 ? 1 : 0) +
      (effectiveSentimentsCount < 3 ? 1 : 0),
    );
  }, [classificationData, availableGroups.length, availableThemes.length]);

  useEffect(() => {
    const imageState = contentData.imageTranscriptionState ?? (contentData.imageTranscription ? 'add' : null);
    const collectedState = contentData.collectedByAIState ?? (contentData.collectedByAI ? 'add' : null);

    setContentBadgeCount(
      [
        contentData.textualSearch.length > 0 ? 1 : 0,
        imageState == null ? 0 : 1,
        collectedState == null ? 0 : 1,
        contentData.publicationID.length > 0 ? 1 : 0,
        contentData.includeMentions ? 1 : 0,
        contentData.contentType.size === ALL_CONTENT_TYPE_IDS.size ? 0 : 1,
      ].reduce((sum, val) => sum + val, 0),
    );
  }, [contentData]);

  useEffect(() => {
    setInteractionsBadgeCount(
      Object.values(interactionsData).filter(isInteractionRangeFiltered).length
    );
  }, [interactionsData]);

  useEffect(() => {
    const { channelsSelection, selectedLocations, languagesSelection, languagesTouched, channelsTouched } = originData;
    // The language and channels selectors start fully selected ("Todos") by default, and clicking
    // the "Todos" option also selects everything - both cases are equivalent to "no filter
    // applied" (nothing is sent to the API for a full selection), so only count them when a
    // partial (not full "select all") selection is active.
    const isPartialSelection = (selection: Record<string, string>, availableCount: number) => {
      const selectedCount = Object.keys(selection).length;
      if (selectedCount === 0) return false;
      // If the available options aren't known yet (e.g. channels still loading), we can't tell
      // whether this is a full "select all" - assume it's filtered rather than hiding a real filter.
      if (availableCount <= 0) return true;
      return selectedCount < availableCount;
    };
    const isLanguagesFiltered = !!languagesTouched && isPartialSelection(languagesSelection, LANGUAGE_OPTIONS.length);
    const isChannelsFiltered = !!channelsTouched && isPartialSelection(channelsSelection, allChannels.length);
    setOriginBadgeCount(
      (isChannelsFiltered ? 1 : 0) +
      (selectedLocations.length > 0 ? 1 : 0) +
      (isLanguagesFiltered ? 1 : 0),
    );
  }, [originData, allChannels.length]);

  // Sync conversationData whenever resolvedConfig changes to ensure only visible fields are kept
  useEffect(() => {
    setConversationData((prevData) => clampConversationDataForConfig(prevData, resolvedConfig));
  }, [resolvedConfig]);

  useEffect(() => {
    const isFieldFiltered = (touched: boolean, sel: Record<string, string>) => touched && Object.keys(sel).length > 0;
    setConversationBadgeCount(
      (isFieldFiltered(conversationData.channelsTouched, conversationData.channels) ? 1 : 0) +
      (isFieldFiltered(conversationData.pagesTouched, conversationData.pages) ? 1 : 0) +
      (isFieldFiltered(conversationData.interactionsTouched, conversationData.interactions) ? 1 : 0) +
      (isFieldFiltered(conversationData.criticalityTouched, conversationData.criticality) ? 1 : 0) +
      (isFieldFiltered(conversationData.statusTouched, conversationData.status) ? 1 : 0) +
      (isFieldFiltered(conversationData.respondentTouched, conversationData.respondent) ? 1 : 0) +
      (isFieldFiltered(conversationData.routingTouched, conversationData.routing) ? 1 : 0) +
      (conversationData.conversationId.trim().length > 0 ? 1 : 0) +
      (conversationData.publicationType.organic || conversationData.publicationType.darkPost ? 1 : 0),
    );
  }, [conversationData]);

  useEffect(() => {
    const { authorsSelection, interestsSelection, profileTypeSelection, followersRange, profileTypeTouched } = authorData;
    // The profile type selector starts fully selected ("Todos") by default, which is visually
    // indistinguishable from an explicit "select all" - rely on profileTypeTouched to tell them
    // apart instead of only checking whether there is a non-empty selection.
    const isProfileTypeFiltered = !!profileTypeTouched && Object.keys(profileTypeSelection).length > 0;
    setAuthorBadgeCount(
      (Object.keys(authorsSelection).length > 0 ? 1 : 0) +
      (Object.keys(interestsSelection).length > 0 ? 1 : 0) +
      (isProfileTypeFiltered ? 1 : 0) +
      (followersRange != null && (followersRange[0] > MIN || followersRange[1] < MAX) ? 1 : 0),
    );
  }, [authorData]);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      fetchConversationPagesByAccount().catch(() => []),
      getChannels().catch(() => []),
    ])
      .then(([pages, fetchedChannels]) => {
        if (!isMounted) return;
        const channelSmartcareMap = buildChannelSmartcareMap(fetchedChannels);
        const activeUniverseId = getActiveUniverseId();
        const smartcareFilteredPages = filterSmartcarePages(pages, channelSmartcareMap, activeUniverseId);
        const mappedPages = mapConnectedPagesAsAuthors(smartcareFilteredPages);

        setConnectedPagesAsAuthors(mappedPages);

        const filteredChannels = filterExcludedChannels(fetchedChannels, excludedChannelKeysRef.current);

        const conversationChannels = Array.from(new Set(smartcareFilteredPages.map((page) => page.channel.toLowerCase())));
        const pageKeys = smartcareFilteredPages.map((page) => mapConversationPageToKey(page.channel, page.id));
        const interactionValues = getInteractionValuesFromChannels(filteredChannels, conversationChannels);

        setAllChannels(filteredChannels);
        setConversationAvailableChannels(conversationChannels);
        setConversationAvailablePages(pageKeys);
        setConversationAvailableInteractions(interactionValues);
        setHasCheckedConversationPages(true);

        // Initialize origin data with all channels if not already set OR if channels are empty.
        // Skip when a "filters" id is pending in the URL - the decode effect below will restore
        // the correct channel selection, and running this here would race with it and wipe it out.
        const shouldInitializeOriginData = !pendingUrlFilterId && (!initialState?.originData ||
          Object.keys(initialState.originData.channelsSelection).length === 0);

        if (shouldInitializeOriginData && filteredChannels.length > 0) {
          const channelIds = filteredChannels.map((ch) => ch.key);
          setOriginData(buildOriginDefaultData(channelIds));
        }
      })
      .catch((error) => {
        console.error('Unexpected error in channels/pages fetch:', error);
        if (!isMounted) return;
        setHasCheckedConversationPages(true);
      });

    return () => {
      isMounted = false;
    };
  }, [initialState?.originData, pendingUrlFilterId]);

  // Origin (open sea) channels must only reflect channels that exist in this account/universe -
  // allChannels is already filtered to exclude excludedChannelKeys, so pruning against it also
  // takes care of dropping any excluded channel that may have come from a saved/URL filter. An
  // empty allChannels can mean either "still loading" or "loaded with none available", so gate on
  // the loaded flag instead of the array length.
  useEffect(() => {
    if (!hasCheckedConversationPages) return;
    setOriginData((prev) => {
      if (!prev.channelsTouched) return prev;
      const filtered = pruneChannelsSelectionToAvailable(prev.channelsSelection, allChannels);
      if (Object.keys(filtered).length === Object.keys(prev.channelsSelection).length) return prev;
      return { ...prev, channelsSelection: filtered };
    });
  }, [allChannels, hasCheckedConversationPages]);

  // Proprietary (conversations) channels must only reflect channels connected to this account -
  // mirrors the origin channels pruning above, gating on the loaded flag for the same reason.
  useEffect(() => {
    if (!hasCheckedConversationPages) return;
    setConversationData((prev) => {
      if (!prev.channelsTouched) return prev;
      const filtered = pruneSelectionToAvailableKeys(prev.channels, conversationAvailableChannels);
      if (Object.keys(filtered).length === Object.keys(prev.channels).length) return prev;
      return { ...prev, channels: filtered };
    });
  }, [conversationAvailableChannels, hasCheckedConversationPages]);

  useEffect(() => {
    if (!hasCheckedConversationPages) return;
    setConversationData((prev) => {
      if (!prev.pagesTouched) return prev;
      const filtered = pruneSelectionToAvailableKeys(prev.pages, conversationAvailablePages);
      if (Object.keys(filtered).length === Object.keys(prev.pages).length) return prev;
      return { ...prev, pages: filtered };
    });
  }, [conversationAvailablePages, hasCheckedConversationPages]);

  useEffect(() => {
    let isMounted = true;
    fetchConversationRoutingByAccount()
      .then((routingSettings) => {
        if (!isMounted || routingSettings.length === 0) return;
        const mapping = routingSettings.reduce<Record<string, string>>((acc, setting) => {
          acc[setting.id] = setting.name;
          return acc;
        }, {});
        setRoutingNameById(mapping);
      })
      .catch(() => {});
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const handleRespondentsOptionsLoaded = (e: Event) => {
      const count = (e as CustomEvent<{ count: number }>).detail?.count ?? 0;
      setConversationAvailableRespondents(count);
    };

    const handleRoutingOptionsLoaded = (e: Event) => {
      const count = (e as CustomEvent<{ count: number }>).detail?.count ?? 0;
      setConversationAvailableRouting(count);
    };

    globalThis.addEventListener(GLOBAL_FILTER_EVENTS.CONVERSATION_RESPONDENTS_OPTIONS_LOADED, handleRespondentsOptionsLoaded);
    globalThis.addEventListener(GLOBAL_FILTER_EVENTS.CONVERSATION_ROUTING_OPTIONS_LOADED, handleRoutingOptionsLoaded);

    return () => {
      globalThis.removeEventListener(GLOBAL_FILTER_EVENTS.CONVERSATION_RESPONDENTS_OPTIONS_LOADED, handleRespondentsOptionsLoaded);
      globalThis.removeEventListener(GLOBAL_FILTER_EVENTS.CONVERSATION_ROUTING_OPTIONS_LOADED, handleRoutingOptionsLoaded);
    };
  }, []);

  const resetToDefaults = useCallback(() => {
    setClassificationData(defaultClassificationData);
    setContentData(defaultValues);
    setContentResetKey((k) => k + 1);
    setInteractionsData(createDefaultInteractionsData());
    periodDataTouchedRef.current = true;
    updatePeriodData(getDefaultPeriodData(isDateRestrictedMode));
    const channelIds = allChannels.map((ch) => ch.key);
    setOriginData(buildOriginDefaultData(channelIds));
    setConversationData({
      ...defaultConversationValues,
      publicationType: { ...defaultConversationValues.publicationType },
    });
    setConversationResetKey((k) => k + 1);
    setAuthorData(getDefaultAuthorData());
    setTextualSearchError('');
    setOpenSections(new Set(['period']));
    setOpenSeaActive(initialOpenSeaActive);
    setProprietaryChannelsActive(initialProprietaryActive);
    setIsNewSearchDirty(false);
  }, [allChannels, initialOpenSeaActive, initialProprietaryActive, isDateRestrictedMode, updatePeriodData]);

  const loadFilterIntoSections = useCallback((filter: SavedFilter) => {
    const rawFilterObj = resolveFilterObj(filter);
    const sanitized = sanitizeSavedFilterObj(rawFilterObj, { groups: availableGroups, themes: availableThemes, tags: availableTags });
    const {
      classData,
      contentData: cd,
      interactionsData: id,
      originData: od,
      isSac,
      conversationData: conv,
      authorData: restoredAuthor,
    } = filterObjToState(sanitized as FilterEncodeData);
    setClassificationData(clampClassificationDataForConfig(classData, resolvedConfig));
    setContentData(clampContentDataForConfig(cd, resolvedConfig));
    setContentResetKey((k) => k + 1);
    setInteractionsData(id);
    setOriginData(clampOriginDataForConfig(od, resolvedConfig));
    setConversationData(clampConversationDataForConfig(conv, resolvedConfig));
    setConversationResetKey((k) => k + 1);
    periodDataTouchedRef.current = true;
    updatePeriodData(undefined);
    setTextualSearchError('');
    setOpenSections(new Set(['period']));
    setIsEditDirty(false);
    setOpenSeaActive(!isSac);
    setProprietaryChannelsActive(isSac);

    if (restoredAuthor) setAuthorData(clampAuthorDataForConfig(restoredAuthor, resolvedConfig));
  }, [availableGroups, availableThemes, availableTags, resolvedConfig, updatePeriodData]);

  const handleTabChange = (tab: 'newSearch' | 'savedFilters') => {
    if (tab === activeTab) return;
    if (tab === 'savedFilters' && activeTab === 'newSearch') {
      if (isNewSearchDirty && !isSaveChecked) {
        setShowUnsavedAlert(true);
        return;
      }
    }
    setEditMode(false);
    setEditingFilter(null);
    setActiveTab(tab);
  };

  const handleAlertContinueWithoutSaving = () => {
    setShowUnsavedAlert(false);
    setIsNewSearchDirty(false);
    resetToDefaults();
    setActiveTab('savedFilters');
  };

  const handleAlertSaveFilter = () => {
    setIsSaveChecked(true);
    setShowUnsavedAlert(false);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.CLOSED));
    }, 300);
  };

  const handleChipChange = (chip: 'openSea' | 'proprietaryChannels') => {
    if (chipsAreLocked) return;
    if (chip === 'proprietaryChannels' && proprietaryChipDisabledReason) return;
    if (chip === 'openSea') {
      setOpenSeaActive(true);
      setProprietaryChannelsActive(false);
      setOpenSections((prev) => { const n = new Set(prev); n.delete('conversations'); return n; });
    }
    if (chip === 'proprietaryChannels') {
      setOpenSeaActive(false);
      setProprietaryChannelsActive(true);
      setOpenSections((prev) => { const n = new Set(prev); n.delete('interactions'); return n; });
      setInteractionsData(createDefaultInteractionsData());
      setInteractionsBadgeCount(0);
      setOriginData((prev) => ({ ...prev, channelsSelection: {}, channelsTouched: false }));
      setContentData((prev) => ({
        ...prev,
        imageTranscription: false,
        imageTranscriptionState: null,
        collectedByAI: false,
        collectedByAIState: null,
        includeMentions: false,
        contentType: new Set(defaultValues.contentType),
      }));
    }
    if (editMode) setIsEditDirty(true);
    else setIsNewSearchDirty(true);
    globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.CHIP_CHANGED, { detail: { chip } }));
  };

  const filterBodyRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Partial<Record<string, HTMLDivElement | null>>>({});
  const [sectionToScrollInto, setSectionToScrollInto] = useState<{ name: string; key: number } | null>(null);

  const handleToggle = useCallback((name: string) => {
    const willOpen = !openSectionsRef.current.has(name);
    setOpenSections((prev) => {
      const n = new Set(prev);
      if (n.has(name)) {
        n.delete(name);
      } else {
        n.add(name);
      }
      return n;
    });
    if (willOpen) {
      setSectionToScrollInto({ name, key: Date.now() });
    }
  }, []);

  // ── scroll the modal body so a newly opened section is fully visible ─────────
  const scrollBodyTo = (container: HTMLDivElement, top: number) => {
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top, behavior: 'smooth' });
    } else {
      container.scrollTop = top;
    }
  };

  useEffect(() => {
    if (!sectionToScrollInto) return;
    const container = filterBodyRef.current;
    const section = sectionRefs.current[sectionToScrollInto.name];
    if (!container || !section) return;

    const frame = requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();

      if (sectionRect.height >= containerRect.height) {
        // Section is taller than the visible area: align its top with the container top.
        const offset = sectionRect.top - containerRect.top + container.scrollTop;
        scrollBodyTo(container, offset);
        return;
      }

      const bottomOverflow = sectionRect.bottom - containerRect.bottom;
      if (bottomOverflow > 0) {
        scrollBodyTo(container, container.scrollTop + bottomOverflow);
        return;
      }

      const topOverflow = containerRect.top - sectionRect.top;
      if (topOverflow > 0) {
        scrollBodyTo(container, container.scrollTop - topOverflow);
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [sectionToScrollInto]);

  // ── ensure period section is always open ────────────────────────────
  useEffect(() => {
    setOpenSections((prev) => {
      if (!prev.has('period')) {
        const n = new Set(prev);
        n.add('period');
        return n;
      }
      return prev;
    });
  }, []);

  // ── auto-scroll modal when items are added to selectors ─────────────
  const previousSelectionsRef = useRef({
    groups: 0,
    themes: 0,
    tags: 0,
    classifiedBy: 0,
    selection: 0,
    channels: 0,
    languages: 0,
    authors: 0,
    interests: 0,
    profileType: 0,
    conversationChannels: 0,
    conversationPages: 0,
    conversationInteractions: 0,
    conversationCriticality: 0,
    conversationStatus: 0,
    conversationRespondent: 0,
    conversationRouting: 0,
  });

  useEffect(() => {
    const container = filterBodyRef.current;
    if (!container) return;

    const countSelections = (selectionMap: SelectionMap | undefined): number => {
      if (!selectionMap) return 0;
      return Object.values(selectionMap).filter((action) => action === 'add').length;
    };

    const currentSelections = {
      groups: countSelections(classificationData.groups),
      themes: countSelections(classificationData.themes),
      tags: countSelections(classificationData.tags),
      classifiedBy: countSelections(classificationData.classifiedBy),
      selection: countSelections(classificationData.selection),
      channels: countSelections(originData.channelsSelection),
      languages: countSelections(originData.languagesSelection),
      authors: countSelections(authorData.authorsSelection),
      interests: countSelections(authorData.interestsSelection),
      profileType: countSelections(authorData.profileTypeSelection),
      conversationChannels: countSelections(conversationData.channels),
      conversationPages: countSelections(conversationData.pages),
      conversationInteractions: countSelections(conversationData.interactions),
      conversationCriticality: countSelections(conversationData.criticality),
      conversationStatus: countSelections(conversationData.status),
      conversationRespondent: countSelections(conversationData.respondent),
      conversationRouting: countSelections(conversationData.routing),
    };

    const hasAddedItem = Object.keys(currentSelections).some(
      (key) => currentSelections[key as keyof typeof currentSelections] > previousSelectionsRef.current[key as keyof typeof previousSelectionsRef.current]
    );

    if (hasAddedItem) {
      requestAnimationFrame(() => {
        const activeElement = document.activeElement;
        if (activeElement && container.contains(activeElement)) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    }

    previousSelectionsRef.current = currentSelections;
  }, [classificationData, originData, authorData, conversationData]);

  const handleContentDataChange = (v: ContentData) => {
    setContentData(v);
    if (textualSearchError) setTextualSearchError('');
    if (editMode) setIsEditDirty(true); else setIsNewSearchDirty(true);
  };

  const handleClassificationChange = (data: ClassificationData) => {
    setClassificationData(data);
    if (editMode) setIsEditDirty(true); else setIsNewSearchDirty(true);
  };

  const handleInteractionsChange = (data: InteractionsData) => {
    setInteractionsData(data);
    if (editMode) setIsEditDirty(true); else setIsNewSearchDirty(true);
  };

  const handlePeriodChange = useCallback((data: PeriodData) => {
    periodDataTouchedRef.current = true;
    updatePeriodData(data);
    if (editMode) setIsEditDirty(true); else setIsNewSearchDirty(true);
  }, [editMode, updatePeriodData]);

  const handleOriginChange = useCallback((data: OriginData) => {
    setOriginData(data);
    if (editMode) setIsEditDirty(true); else setIsNewSearchDirty(true);
  }, [editMode]);

  const handleConversationChange = useCallback((data: ConversationData) => {
    setConversationData(data);
    if (!editMode) setIsNewSearchDirty(true);
  }, [editMode]);

  const handleAuthorChange = useCallback((data: AuthorData) => {
    setAuthorData(data);
    if (!editMode) setIsNewSearchDirty(true);
  }, [editMode]);

  const buildInteractionsPayload = (data: InteractionsData = interactionsData): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};
    for (const key of ['likes', 'shares', 'views', 'comments'] as const) {
      const rangePayload = buildRangePayload(data[key], MIN, MAX);
      if (rangePayload) payload[key] = rangePayload;
    }
    return payload;
  };

  const buildChannelsPayload = (data: OriginData = originData): Record<string, unknown> => {
    const addedChannelIds = Object.entries(data.channelsSelection)
      .filter(([, action]) => action === 'add')
      .map(([id]) => id);
    return (addedChannelIds.length > 0 && addedChannelIds.length < allChannels.length)
      ? { channels: { value: addedChannelIds } }
      : {};
  };

  const buildLanguagesPayload = (data: OriginData = originData): Record<string, unknown> => {
    const addedLangs = Object.entries(data.languagesSelection)
      .filter(([, action]) => action === 'add')
      .map(([iso]) => iso);
    return (addedLangs.length > 0 && addedLangs.length < LANGUAGE_OPTIONS.length)
      ? { langs: { value: addedLangs } }
      : {};
  };

  const buildLocationsPayload = (data: OriginData = originData): Record<string, unknown> => {
    const locationObjects = data.selectedLocations.map((loc) => ({
      ...(loc.city ? { city: loc.city } : {}),
      ...(loc.state ? { state: loc.state } : {}),
      ...(loc.country ? { country: loc.country } : {}),
    }));
    return locationObjects.length > 0 ? { locations: { value: locationObjects } } : {};
  };

  const filterValidItems = (items: SelectionMap, availableItems: string[]): SelectionMap => {
    const availableSet = new Set(availableItems);
    return Object.fromEntries(
      Object.entries(items).filter(([key]) => availableSet.has(key))
    );
  };

  const getTouchedState = (fieldKey: 'groups' | 'themes' | 'tags', data: ClassificationData): boolean => {
    if (fieldKey === 'groups') return data.groupsTouched;
    if (fieldKey === 'themes') return data.themesTouched;
    return true;
  };

  const buildClassificationPayload = (
    fieldKey: 'groups' | 'themes' | 'tags',
    payloadKey: string,
    sendWhenEmpty?: boolean,
    data: ClassificationData = classificationData,
  ): Record<string, unknown> => {
    const items = data[fieldKey];
    const availableList = { groups: availableGroups, themes: availableThemes, tags: availableTags }[fieldKey];
    const validItems = filterValidItems(items, availableList);

    if (Object.keys(validItems).length === 0) {
      const touched = getTouchedState(fieldKey, data);
      return sendWhenEmpty && touched ? { [payloadKey]: { value: [] } } : {};
    }
    const availableCount = availableList.length;
    const addedCount = Object.values(validItems).filter((a) => a === 'add').length;
    if (addedCount === availableCount && addedCount === Object.keys(validItems).length) return {};
    return { [payloadKey]: { value: toFilterValues(validItems) } };
  };

  const buildTagsPayload = (data: ClassificationData = classificationData): Record<string, unknown> => {
    const tags = data.tags;
    const validTags = filterValidItems(tags, availableTags);

    if (!data.tagsIntersection) return buildClassificationPayload('tags', 'tags', undefined, data);

    const addedCount = Object.values(validTags).filter((a) => a === 'add').length;
    const isAllOrNone = Object.keys(validTags).length === 0 ||
      (addedCount === availableTags.length && addedCount === Object.keys(validTags).length);
    if (isAllOrNone) return availableTags.length > 0
      ? { tags: { value: availableTags } }
      : {};
    return buildClassificationPayload('tags', 'tags', undefined, data);
  };

  const buildAnnotatorsPayload = (data: ClassificationData = classificationData): Record<string, unknown> => {
    const addedEditors = Object.entries(data.classifiedBy)
      .filter(([, action]) => action === 'add')
      .map(([name]) => name);
    const isClassDate = periodData?.dataType === 'classification_date';
    const allAnnotatorIds = classifiedByItems
      .map((item) => (typeof item === 'string' ? item : item.key))
      .map((id) => id.trim())
      .filter((id) => /^\d+$/.test(id));
    const annotatorsWhenClassDate = addedEditors.length > 0 ? addedEditors : allAnnotatorIds;
    const annotatorsValues = isClassDate ? annotatorsWhenClassDate : [];
    return annotatorsValues.length > 0 ? { annotators: { value: annotatorsValues } } : {};
  };

  const buildEditedByPayload = (data: ClassificationData = classificationData): Record<string, unknown> => {
    const addedEditors = Object.entries(data.classifiedBy)
      .filter(([, action]) => action === 'add')
      .map(([name]) => name);
    const isClassDate = periodData?.dataType === 'classification_date';
    const isAllSelected = classifiedByItems.length > 0 && addedEditors.length >= classifiedByItems.length;
    return !isClassDate && addedEditors.length > 0 && !isAllSelected
      ? { edited_by: { value: addedEditors } }
      : {};
  };

  const buildSelectionPayload = (data: ClassificationData = classificationData): Record<string, unknown> => {
    const selectedSelectionName = Object.entries(data.selection ?? {})
      .find(([, action]) => action === 'add')?.[0];
    return selectedSelectionName ? { selection: { value: selectedSelectionName } } : {};
  };

  const buildSentimentsPayload = (data: ClassificationData = classificationData): Record<string, unknown> => {
    const { sentiments, sentimentsTouched } = data;
    // Untouched + empty is the clean/default state (all sentiments implicitly marked): no filter applied.
    if (!sentimentsTouched && sentiments.size === 0) return {};
    const polarityValues = [...sentiments]
      .map((s) => NORMALIZED_SENTIMENT_TO_POLARITY[s])
      .filter(Boolean);
    // All 3 sentiments marked = same as the clean/default state, no filter is applied.
    if (polarityValues.length >= 3) return {};
    // Explicit empty selection represents posts with no sentiment classification.
    return { polarities: { value: polarityValues } };
  };

  const isMetricsContextMode = (): boolean => {
    return isMetricsFieldRestrictedContext(resolvedConfig.mfeMode, resolvedConfig.allowedChips)
      || isDateRestrictedMode;
  };

  const buildConversationPayloadIfVisible = (
    isMetricsContext: boolean,
    data: ConversationData = conversationData,
  ): Record<string, unknown> => {
    if (!(resolvedConfig.mfeMode === 'date-and-conversations' || proprietaryChannelsActive)) return {};

    const payload = buildConversationPayload(
      data,
      routingNameById,
      (!isMetricsContext && proprietaryChannelsActive) ? {
        channels: conversationAvailableChannels,
        pages: conversationAvailablePages,
        interactions: conversationAvailableInteractions,
      } : undefined,
      {
        status: 6,
        criticality: 4,
        respondents: conversationAvailableRespondents,
        routing: conversationAvailableRouting,
        channels: conversationAvailableChannels.length,
        pages: conversationAvailablePages.length,
        interactions: conversationAvailableInteractions.length,
      },
    );

    return payload;
  };

  const buildClassificationPayloads = (data: ClassificationData = classificationData): Record<string, unknown> => {
    const payload: Record<string, unknown> = {};

    if (!resolvedConfig.disableClassificationGroups) {
      Object.assign(payload, buildClassificationPayload('groups', 'names', true, data));
      Object.assign(payload, buildClassificationPayload('themes', 'themes', true, data));
    }

    Object.assign(payload, buildTagsPayload(data));

    if (data.tagsIntersection) {
      Object.assign(payload, { intersection: { value: ['tags'] } });
    }

    Object.assign(payload, buildEditedByPayload(data));
    Object.assign(payload, buildAnnotatorsPayload(data));
    Object.assign(payload, buildSelectionPayload(data));
    Object.assign(payload, buildSentimentsPayload(data));
    Object.assign(payload, buildStatusPayload(data.status));

    return payload;
  };

  const buildEncodePayload = (): Record<string, unknown> => {
    const isMetricsContext = isMetricsContextMode();
    const annotationRange = periodData?.dataType === 'classification_date'
      ? { annotation_range: { value: true } }
      : {};
    const isSac = isMetricsContext ? false : !openSeaActive;

    const sectionData = preserveHiddenSectionsForPersistence(
      { classificationData, contentData, interactionsData, originData, conversationData, authorData },
      preservedHiddenStateRef.current,
      resolvedConfig,
    );

    let effectiveAuthorData = sectionData.authorData;
    if (effectiveAuthorData.myPagesFilter && effectiveAuthorData.myPagesFilter !== 'all') {
      let filteredPages = connectedPagesAsAuthors;

      if (sectionData.conversationData.pagesTouched) {
        const selectedPageKeySet = new Set(
          Object.entries(sectionData.conversationData.pages)
            .filter(([, action]) => String(action).toLowerCase() === 'add')
            .map(([key]) => key.toLowerCase()),
        );
        filteredPages = connectedPagesAsAuthors.filter((page) => {
          const channelKey = page.channel.toLowerCase();
          const uidKey = String(page.uid).toLowerCase();
          const prefixedKey = `${channelKey}-${uidKey}`;
          
          return selectedPageKeySet.has(prefixedKey) || selectedPageKeySet.has(uidKey);
        });
      }

      effectiveAuthorData = applyMyPagesFilterToData(effectiveAuthorData, effectiveAuthorData.myPagesFilter, filteredPages);
    }
    const payload: Record<string, unknown> = {
      is_sac: isSac,
      ...annotationRange,
      ...buildInteractionsPayload(sectionData.interactionsData),
      ...buildContentPayload(sectionData.contentData, isSac),
      ...buildChannelsPayload(sectionData.originData),
      ...buildLanguagesPayload(sectionData.originData),
      ...buildLocationsPayload(sectionData.originData),
      ...buildClassificationPayloads(sectionData.classificationData),
      ...buildConversationPayloadIfVisible(isMetricsContext, sectionData.conversationData),
      ...buildAuthorEncodePayload(effectiveAuthorData, profileTypeGenderMap, availableProfileTypesKeys.length),
    };

    // Channels/pages aren't user-selectable fields in date-and-conversations mode - never send
    // them, regardless of which section (origin or conversations) they'd otherwise come from.
    if (resolvedConfig.mfeMode === 'date-and-conversations') {
      delete payload.channels;
      delete payload.pages;
    }

    return payload;
  };

  const applyFilterFromSavedFilter = async () => {
    const filter = savedFilters.find((f) => f.id === selectedFilter);
    if (!filter) {
      throw new Error('FILTER_NOT_FOUND');
    }
    
    // Decode the filter from its ID (hash) to get the filterObj
    // The filterObj is no longer stored in the datastore, it must be decoded
    // from the filterId (which is the hash of the content)
    let filterObj = filter.filterObj;
    
    if (!filterObj) {
      const decoded = await decodeFilter(filter.id);
      if (!decoded) {
        throw new Error('FILTER_INVALID');
      }
      filterObj = decoded;
    }
    
    const sanitizedFilterObj = sanitizeSavedFilterObj(filterObj, { groups: availableGroups, themes: availableThemes, tags: availableTags });
    let filterId = await encodeFilter(sanitizedFilterObj);

    try {
      const filterJsonString = JSON.stringify(sanitizedFilterObj);
      const response = await globalSavedFiltersService.saveGlobalFilter(filterJsonString, filterId);
      // If the returned id differs from sent filterId, the filter already existed.
      // Use the returned id for subsequent operations.
      if (response.id && response.id !== filterId) {
        filterId = response.id;
      }
    } catch (error) {
      console.error('Failed to save global filter after encode:', error);
      throw new Error('FILTER_SAVE_FAILED');
    }

    // Convert the saved filter to internal state and persist it to localStorage
    // so that when the modal is reopened, it loads the applied filter
    try {
      const {
        classData,
        contentData: cd,
        interactionsData: id,
        originData: od,
        isSac,
        conversationData: conv,
        authorData: restoredAuthor,
      } = filterObjToState(sanitizedFilterObj as FilterEncodeData);

      localStorage.setItem(
        FILTER_STORAGE_KEY,
        serializeFilterState({
          openSeaActive: !isSac,
          proprietaryChannelsActive: isSac,
          classificationData: clampClassificationDataForConfig(classData, resolvedConfig),
          contentData: clampContentDataForConfig(cd, resolvedConfig),
          interactionsData: id,
          originData: clampOriginDataForConfig(od, resolvedConfig),
          conversationData: clampConversationDataForConfig(conv, resolvedConfig),
          periodData: undefined,
          authorData: restoredAuthor ? clampAuthorDataForConfig(restoredAuthor, resolvedConfig) : undefined,
        }),
      );
    } catch (error) {
      console.error('Failed to save filter state to localStorage:', error);
    }

    syncLegacyDateRangeSession(periodDataRef.current?.formattedDateRange);
    saveAuthorMyPagesFilterByFilterId(filterId, authorData.myPagesFilter ?? 'all');
    globalThis.dispatchEvent(
      new CustomEvent(GLOBAL_FILTER_EVENTS.APPLIED, {
        detail: {
          dateRange: periodDataRef.current?.formattedDateRange,
          filterId,
        },
      }),
    );
    handleClose();
  };

  const validateTextualSearchInput = (): boolean => {
    const searchString = contentData?.textualSearch || '';
    const result = validateTextualSearch(searchString);
    if (searchString.trim().length > 0 && !result.isValid && result.errorMessageKey) {
      setTextualSearchError(t(result.errorMessageKey));
      setOpenSections((prev) => { const n = new Set(prev); n.add('content'); return n; });
      return false;
    }
    setTextualSearchError('');
    return true;
  };

  const encodeAndSaveFilterPayload = async (): Promise<{filterId: string; encodePayload: Record<string, unknown>}> => {
    const encodePayload = buildEncodePayload();
    let filterId: string;
    try {
      filterId = await encodeFilter(encodePayload);
      try {
        const filterJsonString = JSON.stringify(encodePayload);
        const response = await globalSavedFiltersService.saveGlobalFilter(filterJsonString, filterId);
        // If the returned id differs from sent filterId, the filter already existed.
        // Use the returned id for subsequent operations.
        if (response.id && response.id !== filterId) {
          filterId = response.id;
        }
      } catch (error) {
        console.error('Failed to save global filter after encode:', error);
      }
    } catch {
      console.info('Failed to encode filter for saving to localStorage or dispatching event');
      throw new Error('Failed to encode filter');
    }
    return {filterId, encodePayload};
  };

  const saveFilterStateToLocalStorage = () => {
    try {
      const sectionData = preserveHiddenSectionsForPersistence(
        { classificationData, contentData, interactionsData, originData, conversationData, authorData },
        preservedHiddenStateRef.current,
        resolvedConfig,
      );
      localStorage.setItem(
        FILTER_STORAGE_KEY,
        serializeFilterState({
          openSeaActive,
          proprietaryChannelsActive,
          classificationData: sectionData.classificationData,
          contentData: sectionData.contentData,
          interactionsData: sectionData.interactionsData,
          originData: sectionData.originData,
          conversationData: sectionData.conversationData,
          periodData: periodDataRef.current,
          authorData: sectionData.authorData,
        }),
      );
    } catch (error) {
      console.error('Failed to save filter state to localStorage:', error);
    }
  };

  const dispatchFilterAppliedEvent = (filterId: string) => {
    const currentPeriod = periodDataRef.current;

    syncLegacyDateRangeSession(currentPeriod?.formattedDateRange);
    saveAuthorMyPagesFilterByFilterId(filterId, authorData.myPagesFilter ?? 'all');
    globalThis.dispatchEvent(
      new CustomEvent(GLOBAL_FILTER_EVENTS.APPLIED, {
        detail: {
          dateRange: currentPeriod?.formattedDateRange,
          filterId,
        },
      }),
    );
  };

  const handleApply = async () => {
    if (isActionInProgress) return;
    setIsActionInProgress(true);

    if (activeTab === 'savedFilters' && selectedFilter) {
      try {
        await applyFilterFromSavedFilter();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'UNKNOWN_ERROR';
        
        let title = t('globalFilter.applyFilterErrorTitle', 'Erro ao aplicar filtro');
        let text = t('globalFilter.applyFilterErrorText', 'Não foi possível aplicar o filtro. Tente novamente.');
        
        if (errorMessage === 'FILTER_NOT_FOUND') {
          title = t('globalFilter.filterNotFoundTitle', 'Filtro não encontrado');
          text = t('globalFilter.filterNotFoundText', 'O filtro selecionado não foi encontrado.');
        } else if (errorMessage === 'FILTER_INVALID') {
          title = t('globalFilter.filterInvalidTitle', 'Filtro inválido');
          text = t('globalFilter.filterInvalidText', 'O filtro selecionado está corrompido ou incompleto.');
        } else if (errorMessage === 'FILTER_SAVE_FAILED') {
          title = t('globalFilter.filterSaveFailedTitle', 'Erro ao salvar filtro');
          text = t('globalFilter.filterSaveFailedText', 'Não foi possível salvar o filtro no servidor.');
        }

        await showToast({
          variant: 'error',
          title,
          text,
          icon: 'error',
          duration: 8,
        });
      } finally {
        setIsActionInProgress(false);
      }
      return;
    }

    if (!validateTextualSearchInput()) {
      setIsActionInProgress(false);
      return;
    }

    try {
      const {filterId} = await encodeAndSaveFilterPayload();
      saveFilterStateToLocalStorage();
      dispatchFilterAppliedEvent(filterId);
      handleClose();
    } catch {
      // Error already logged in encodeAndSaveFilterPayload
      setIsActionInProgress(false);
      return;
    }
  };

  const handleClear = () => {
    if (isActionInProgress) return;
    setFilterName('');
    resetToDefaults();
    try { localStorage.removeItem(FILTER_STORAGE_KEY); } catch { /* ignore */ }
    globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.CLEARED));
  };

  const handleZoom = async () => {
    if (isActionInProgress) return;
    setIsActionInProgress(true);

    const encodePayload = buildEncodePayload();
    let filterId: string;
    try {
      filterId = await encodeFilter(encodePayload);
    } catch (error) {
      console.error('Failed to encode filter for zoom:', error);
      setIsActionInProgress(false);
      return;
    }

    // Save the filter to backend with hash
    try {
      const filterJsonString = JSON.stringify(encodePayload);
      const response = await globalSavedFiltersService.saveGlobalFilter(filterJsonString, filterId);
      // If the returned id differs from sent filterId, the filter already existed.
      // Use the returned id for subsequent operations.
      if (response.id && response.id !== filterId) {
        filterId = response.id;
      }
    } catch (error) {
      console.error('Failed to save global filter after encode:', error);
      // Continue even if save fails - encode was successful
    }

    globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.ZOOM_CLICKED, { detail: { filterId } }));
    handleClose();
  };

  const handleSave = async (name: string) => {
    const isDuplicateName = savedFilters.some((f) => f.name.toLowerCase() === name.toLowerCase());
    if (isDuplicateName) { 
      setSaveResult('duplicate'); 
      setContentDuplicateData(null);
      return; 
    }

    try {
      const filterObj = buildEncodePayload();
      let filterId = await encodeFilter(filterObj);

      // The GlobalFilter record must exist before /saved-filters will accept it,
      // so this failure must not be swallowed - propagate to the outer catch.
      const filterJsonString = JSON.stringify(filterObj);
      const response = await globalSavedFiltersService.saveGlobalFilter(filterJsonString, filterId);

      // CRITICAL: If the returned id differs from sent filterId, the filter already
      // existed with that id. Check if another saved filter already uses this content.
      if (response.id && response.id !== filterId) {
        filterId = response.id;
        
        // Save again with the correct ID to ensure hash is associated with the right ID
        await globalSavedFiltersService.saveGlobalFilter(filterJsonString, filterId);
      }

      // CRITICAL: Check for content duplicate BEFORE creating saved filter
      // The 'id' field in SavedFilter is the GlobalFilter ID (hash of content)
      const existingFilter = savedFilters.find((f) => f.id === filterId);
      
      if (existingFilter) {
        // Content duplicate detected - another saved filter already has this content
        setSaveResult('contentDuplicate');
        setContentDuplicateData({ existingFilterName: existingFilter.name });
        return;
      }

      await createSavedFilter(name, filterId, filterObj);
      invalidateSavedFiltersCache(); // Clear cache after successful save
      setSaveResult('success');
      setContentDuplicateData(null);
      setIsNewSearchDirty(false);
      globalThis.dispatchEvent(new CustomEvent(GLOBAL_FILTER_EVENTS.SAVE_CLICKED, { detail: { name } }));
      // Refresh the list in the background - don't block the UI on it
      fetchSavedFilters(true).catch(() => {});
    } catch (error) {
      console.error('🔴 Erro ao salvar:', error);
      setSaveResult('error');
      setContentDuplicateData(null);
    }
  };

  const handleSaveResultShown = useCallback(() => {
    setSaveResult(null);
    setContentDuplicateData(null);
  }, []);

  useEffect(() => {
    if (!pendingUrlFilterId) return;
    decodePendingUrlFilterOnce()
      .then((decoded) => {
        if (!decoded) return;
        const {
          classData,
          contentData: cd,
          interactionsData: id,
          originData: od,
          isSac,
          conversationData: conv,
          authorData: restoredAuthor,
        } = filterObjToState(decoded);

        const restoredMyPagesFilter = loadAuthorMyPagesFilterByFilterId(pendingUrlFilterId);
        setOpenSeaActive(!isSac);
        setProprietaryChannelsActive(isSac);
        setClassificationData(classData);
        setContentData(cd);
        setContentResetKey((k) => k + 1);
        setInteractionsData(id);
        // Channels may not be loaded yet - if so, leave the selection untouched and let the pruning
        // effects above handle it once the lists actually arrive (an empty ref here is ambiguous
        // between "still loading" and "loaded with none available").
        const channelsHaveLoaded = hasCheckedConversationPagesRef.current;
        const prunedChannelsSelection = channelsHaveLoaded
          ? pruneChannelsSelectionToAvailable(od.channelsSelection, allChannelsRef.current)
          : od.channelsSelection;
        setOriginData({ ...od, channelsSelection: prunedChannelsSelection });
        const prunedConversationChannels = channelsHaveLoaded
          ? pruneSelectionToAvailableKeys(conv.channels, conversationAvailableChannelsRef.current)
          : conv.channels;
        setConversationData({ ...conv, channels: prunedConversationChannels });
        setConversationResetKey((k) => k + 1);

        if (restoredAuthor) {
          setAuthorData({
            ...restoredAuthor,
            myPagesFilter: restoredMyPagesFilter ?? restoredAuthor.myPagesFilter ?? 'all',
          });
          return;
        }

        if (restoredMyPagesFilter) {
          setAuthorData((prev) => ({ ...prev, myPagesFilter: restoredMyPagesFilter }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEditFilter = async () => {
    const filter = savedFilters.find((f) => f.id === selectedFilter);
    if (!filter) return;

    // Decode the filter from its ID (hash) to get the filterObj
    // The filterObj is no longer stored in the datastore, it must be decoded
    // from the filterId (which is the hash of the content)
    let filterObj = filter.filterObj;
    
    if (!filterObj) {
      const decoded = await decodeFilter(filter.id);
      if (!decoded) {
        console.error('Failed to decode filter:', filter.id);
        return;
      }
      filterObj = decoded;
    }

    // Create a filter object with the decoded filterObj for loadFilterIntoSections
    const filterWithObj = { ...filter, filterObj };
    
    setEditingFilter(filter);
    loadFilterIntoSections(filterWithObj);
    setEditMode(true);
  };

  const handleBackFromEdit = () => {
    setEditMode(false);
    setEditingFilter(null);
    resetToDefaults();
  };

  const handleSaveChanges = async () => {
    if (!editingFilter) return;

    const encodePayload = buildEncodePayload();
    try {
      const id = editingFilter.id;
      let editingFilterId = await encodeFilter(encodePayload);

      if (!editingFilterId) throw new Error('Failed to encode filter');

      // The GlobalFilter record must exist before /saved-filters will accept an
      // update, so this failure must not be swallowed - propagate to the outer catch.
      const filterJsonString = JSON.stringify(encodePayload);
      const response = await globalSavedFiltersService.saveGlobalFilter(filterJsonString, editingFilterId);

      // CRITICAL: If the returned id differs from sent filterId, the filter already
      // existed with that id. We MUST use the returned id for updateSavedFilter.
      if (response.id && response.id !== editingFilterId) {
        editingFilterId = response.id;
        // Save again with the correct ID to ensure hash is associated with the right ID
        await globalSavedFiltersService.saveGlobalFilter(filterJsonString, editingFilterId);
      }

      // CRITICAL: Check if another saved filter already has this content
      // Exclude the filter being edited from the check
      const existingFilter = savedFilters.find((f) => f.id === editingFilterId && f.id !== id);

      if (existingFilter) {
        // Content duplicate detected - another saved filter already has this content

        await showToast({
          variant: 'warning',
          title: t('globalFilter.saveFilterContentDuplicateTitle', 'Conteúdo duplicado!'),
          text: t('globalFilter.saveFilterContentDuplicateText', "Um filtro salvo com este conteúdo já existe com o nome '{{filterName}}'. Por favor, edite o filtro existente ou crie um filtro com conteúdo diferente.", { filterName: existingFilter.name }),
          icon: 'info',
          duration: 10,
        });
        return;
      }

      document.dispatchEvent(
        new CustomEvent(GLOBAL_FILTER_EVENTS.EDIT_SAVE_CLICKED, { detail: { filterId: id, editingFilterId } }),
      );
      await updateSavedFilter(id, editingFilterId, editingFilter.name, encodePayload);
      invalidateSavedFiltersCache(); // Clear cache after successful update
      handleBackFromEdit();
      await showToast({
        variant: 'success',
        title: t('globalFilter.saveChangesSuccessTitle', 'Alterações salvas com sucesso'),
        text: t('globalFilter.saveChangesSuccessText', 'O filtro foi atualizado.'),
        icon: 'check',
        duration: 8,
      });
      // Refresh the list in the background - don't block returning to the selector on it
      fetchSavedFilters(true).catch(() => {});
    } catch (error) {
      console.error('🔵 Erro ao salvar alterações:', error);
      await showToast({
        variant: 'error',
        title: t('globalFilter.saveChangesErrorTitle', 'Não foi possível salvar as alterações'),
        text: t('globalFilter.saveChangesErrorText', 'Tente novamente mais tarde.'),
        icon: 'error',
        duration: 8,
      });
    }
  };

  const handleDeleteFilter = async () => {
    if (!editingFilter) return;
    try {
      await deleteSavedFilter(editingFilter.id);
      invalidateSavedFiltersCache(); // Clear cache after successful delete
      document.dispatchEvent(
        new CustomEvent(GLOBAL_FILTER_EVENTS.DELETE_CLICKED, { detail: { filterId: editingFilter.id } }),
      );
      setSelectedFilter('');
      handleBackFromEdit();
      await showToast({
        variant: 'success',
        title: t('globalFilter.deleteFilterSuccessTitle', 'Filtro excluído com sucesso'),
        text: t('globalFilter.deleteFilterSuccessText', 'O filtro foi removido da lista de filtros salvos.'),
        icon: 'check',
        duration: 8,
      });
      // Refresh the list in the background - don't block returning to the selector on it
      fetchSavedFilters(true).catch(() => {});
    } catch {
      await showToast({
        variant: 'error',
        title: t('globalFilter.deleteFilterErrorTitle', 'Não foi possível excluir o filtro'),
        text: t('globalFilter.deleteFilterErrorText', 'Tente novamente mais tarde.'),
        icon: 'error',
        duration: 8,
      });
    }
  };

  const openSeaFilters = useMemo(
    () => savedFilters
      .filter((f) => {
        const filterObj = resolveKnownFilterObj(f, decodedFilterObjs);
        // Unclassified until the lazy decode above resolves - never default to open sea.
        return filterObj !== undefined && !filterObj.is_sac;
      })
      .map((f) => ({ value: f.id, label: f.name })),
    [savedFilters, decodedFilterObjs],
  );

  const proprietaryFilters = useMemo(
    () => savedFilters
      .filter((f) => {
        const filterObj = resolveKnownFilterObj(f, decodedFilterObjs);
        return filterObj?.is_sac === true;
      })
      .map((f) => ({ value: f.id, label: f.name })),
    [savedFilters, decodedFilterObjs],
  );

  const showEditableSections = activeTab === 'newSearch' || editMode;

  return (
    <>
      <div className="global-filter-backdrop" />
      <div className={`global-filter${isClosing ? ' global-filter--closing' : ''}`}>
        <button
          type="button"
          className="global-filter__close-overlay"
          onClick={handleClose}
          onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
          aria-label={t('globalFilter.closeOverlay', 'Close filter')}
          data-testid="global-filter-overlay"
        />

        {showUnsavedAlert && (
          <UnsavedChangesAlert
            onContinueWithoutSaving={handleAlertContinueWithoutSaving}
            onSaveFilter={handleAlertSaveFilter}
          />
        )}

        <div className="global-filter__drawer">
          <div className="global-filter__body" ref={filterBodyRef}>
            <FilterHeader
              activeTab={activeTab}
              onTabChange={handleTabChange}
              openSeaActive={openSeaActive}
              proprietaryChannelsActive={proprietaryChannelsActive}
              onChipChange={handleChipChange}
              openSeaFilters={openSeaFilters}
              proprietaryFilters={proprietaryFilters}
              isLoadingFilters={isLoadingFilters}
              selectedFilter={selectedFilter}
              onFilterSelect={setSelectedFilter}
              onClose={handleClose}
              editMode={editMode}
              editingFilterName={editingFilter?.name}
              onBack={handleBackFromEdit}
              hideChipsRow={resolvedConfig.hideChipsRow || chipsAreLocked}
              chipsAreLocked={chipsAreLocked}
              proprietaryChipDisabledReason={proprietaryChipDisabledReason}
              hideSavedFilters={resolvedConfig.hideSavedFilters}
            />

            {showEditableSections && (
              <>
                {sectionIsVisible('period') && (
                  <div ref={(el) => { sectionRefs.current.period = el; }}>
                    <FilterSection
                      title={t('globalFilter.period')}
                      description={t('globalFilter.periodDescription')}
                      badgeCount={4}
                      isOpen={openSections.has('period')}
                      onToggle={() => handleToggle('period')}
                    >
                      <PeriodContent
                        values={periodData}
                        onChange={handlePeriodChange}
                        limit3Months={isDateRestrictedMode}
                        lockedToLast24h={resolvedConfig.lockPeriodTo24h}
                      />
                    </FilterSection>
                  </div>
                )}

                {sectionIsVisible('classification') && (
                  <div ref={(el) => { sectionRefs.current.classification = el; }}>
                    <FilterSection
                      title={t('globalFilter.classification')}
                      description={t('globalFilter.classificationDescription')}
                      badgeCount={classificationBadgeCount}
                      isOpen={openSections.has('classification')}
                      onToggle={() => handleToggle('classification')}
                    >
                      <ClassificationContent
                        values={classificationData}
                        onCountChange={setClassificationBadgeCount}
                        onChange={handleClassificationChange}
                        classifiedByItems={classifiedByItems}
                        disableGroups={resolvedConfig.disableClassificationGroups}
                      />
                    </FilterSection>
                  </div>
                )}

                {sectionIsVisible('content') && (
                  <div ref={(el) => { sectionRefs.current.content = el; }}>
                    <FilterSection
                      title={t('globalFilter.content')}
                      description={t('globalFilter.contentDescription')}
                      badgeCount={contentBadgeCount}
                      isOpen={openSections.has('content')}
                      onToggle={() => handleToggle('content')}
                    >
                      <ContentSection
                        key={contentResetKey}
                        initialValues={contentData}
                        onChange={handleContentDataChange}
                        errorMessage={textualSearchError}
                        onCountChange={setContentBadgeCount}
                        disableProprietaryChannelsFilters={proprietaryChannelsActive}
                        disableStilingueEyes={resolvedConfig.disableContentStilingueEyes}
                        disableIncludeMentions={resolvedConfig.disableContentIncludeMentions}
                        showPublicationType={resolvedConfig.showPublicationTypeInContent}
                      />
                    </FilterSection>
                  </div>
                )}

                {sectionIsVisible('interactions') && (
                  <div ref={(el) => { sectionRefs.current.interactions = el; }}>
                    <FilterSection
                      title={t('globalFilter.interactions')}
                      description={t('globalFilter.interactionsDescription')}
                      badgeCount={interactionsBadgeCount}
                      isOpen={openSections.has('interactions')}
                      onToggle={() => handleToggle('interactions')}
                      disabled={chipsShouldControlSections && proprietaryChannelsActive}
                    >
                      <InteractionsContent
                        values={interactionsData}
                        onChange={handleInteractionsChange}
                        onCountChange={setInteractionsBadgeCount}
                      />
                    </FilterSection>
                  </div>
                )}

                {sectionIsVisible('conversations') && (
                  <div ref={(el) => { sectionRefs.current.conversations = el; }}>
                    <FilterSection
                      title={t('globalFilter.conversations')}
                      description={t('globalFilter.conversationsDescription')}
                      badgeCount={conversationBadgeCount}
                      isOpen={openSections.has('conversations')}
                      onToggle={() => handleToggle('conversations')}
                      disabled={chipsShouldControlSections && !proprietaryChannelsActive}
                    >
                      <ConversationContent
                        key={conversationResetKey}
                        values={conversationData}
                        onChange={handleConversationChange}
                        onCountChange={setConversationBadgeCount}
                        visibleFields={resolvedConfig.visibleConversationFields}
                        disableOtherConversationFields={resolvedConfig.disableOtherConversationFields}
                      />
                    </FilterSection>
                  </div>
                )}

                {sectionIsVisible('context') && (
                  <div ref={(el) => { sectionRefs.current.context = el; }}>
                    <FilterSection
                      title={t('globalFilter.context')}
                      description={t('globalFilter.contextDescription')}
                      badgeCount={originBadgeCount}
                      isOpen={openSections.has('context')}
                      onToggle={() => handleToggle('context')}
                    >
                      <OriginContent
                        values={originData}
                        onChange={handleOriginChange}
                        onCountChange={setOriginBadgeCount}
                        availableChannels={channelOptions}
                        channelSelectorDisabled={proprietaryChannelsActive || resolvedConfig.disableOriginChannels}
                      />
                    </FilterSection>
                  </div>
                )}

                {sectionIsVisible('author') && (
                  <div ref={(el) => { sectionRefs.current.author = el; }}>
                    <FilterSection
                      title={t('globalFilter.author')}
                      description={t('globalFilter.authorDescription')}
                      badgeCount={authorBadgeCount}
                      isOpen={openSections.has('author')}
                      onToggle={() => handleToggle('author')}
                    >
                      <AuthorContent
                        values={authorData}
                        onChange={handleAuthorChange}
                        onCountChange={setAuthorBadgeCount}
                        disableFollowers={resolvedConfig.disableAuthorFollowers}
                        connectedPages={filteredConnectedPages}
                        disableMyPages={!proprietaryChannelsActive}
                      />
                    </FilterSection>
                  </div>
                )}
              </>
            )}
          </div>

          <FilterFooter
            isExportMode={isExportMode}
            isSaveChecked={isSaveChecked}
            onToggleSave={setIsSaveChecked}
            onClear={handleClear}
            onZoom={handleZoom}
            onApply={handleApply}
            onSave={handleSave}
            activeTab={activeTab}
            onEdit={handleEditFilter}
            selectedFilter={selectedFilter}
            filterName={filterName}
            onFilterNameChange={setFilterName}
            saveResult={saveResult}
            onSaveResultShown={handleSaveResultShown}
            contentDuplicateData={contentDuplicateData}
            editMode={editMode}
            isSaveChangesDisabled={!isEditDirty}
            onSaveChanges={handleSaveChanges}
            onDeleteFilter={handleDeleteFilter}
            hideZoom={resolvedConfig.hideZoom}
            hideSavedFilters={resolvedConfig.hideSavedFilters}
            areActionsDisabled={isActionInProgress}
          />
        </div>
      </div>
    </>
  );
};
